import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ImportWizard from './ImportWizard';
import OperatorVerification from './OperatorVerification';
import CAVerification from './CAVerification';
import SimpleCAImport from './SimpleCAImport';
import MsisdnIcoFixModal from './MsisdnIcoFixModal';
import BulkExport from './BulkExport';
import IcoOwnerChecker from './IcoOwnerChecker';
import BulkContactImport from './BulkContactImport';
import PageHeader from './PageHeader';
import AutoVerificationModal from './AutoVerificationModal';
import VraceniDtb from './VraceniDtb';
import { API_URL } from '../config';
import { fetchCompanyFromARES } from '../utils/ares';
import { useRolePermissions } from '../hooks/useRolePermissions';

const DEFAULT_COLUMN_FILTERS = {
  name: '', ico: '', address: '', kraj: '', operator: '', status: '', contact: '',
  phone: '', email: '', bmsl: '', forecast: '', ca: '', executive: '',
  prm: '', closedDate: '', contractLength: '', personType: '', nextActionDate: '', owner: '', signal: ''
};

// Stejná logika jako v CompanyForm – pokud název končí tečkou, je to právnická osoba
const detectPersonType = (companyName) => {
  if (!companyName || typeof companyName !== 'string') return '';
  return companyName.trim().endsWith('.') ? 'Právnická osoba' : 'Fyzická osoba';
};

// ====== Module-level helpers — stabilní, nevyvolávají re-render ======
const ROLE_GROUP_CONFIG = [
  { role: 'admin', label: '👑 Administrátoři' },
  { role: 'database', label: '🗄️ Databáze' },
  { role: 'contacts', label: '📋 Kontakty' },
];

function groupUsersByRole(userList, searchText = '') {
  const knownRoles = ROLE_GROUP_CONFIG.map(g => g.role);
  const filtered = searchText
    ? userList.filter(u =>
        (u.username || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (u.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchText.toLowerCase())
      )
    : userList;
  const groups = {};
  filtered.forEach(u => {
    const key = knownRoles.includes(u.role) ? u.role : 'sales';
    if (!groups[key]) groups[key] = [];
    groups[key].push(u);
  });
  const result = ROLE_GROUP_CONFIG
    .filter(g => groups[g.role]?.length > 0)
    .map(g => ({ role: g.role, label: g.label, users: groups[g.role] }));
  if (groups.sales?.length > 0) {
    result.push({ role: 'sales', label: '💼 Obchodníci', users: groups.sales });
  }
  return result;
}

// Izolovaný dropdown — vlastní stav → re-render se nepropaguje do rodiče
function SearchableUserDropdown({ value, onChange, users, specialOptions = [], isDarkMode, compact = false, className = '' }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const selectedLabel =
    specialOptions.find(o => o.value === value)?.label ||
    (users.find(u => u._id === value) ? (users.find(u => u._id === value).name || users.find(u => u._id === value).username) : null) ||
    '-- Vyberte uživatele --';

  const groups = groupUsersByRole(users, search);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        className={`${
          compact ? 'px-3 py-1 min-w-44' : 'w-full px-3 py-2 h-10 rounded-md'
        } border rounded text-left flex items-center justify-between text-sm ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
            : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
        }`}
      >
        <span className="truncate flex-1">{selectedLabel}</span>
        <span className="ml-2 flex-shrink-0 text-gray-400">▾</span>
      </button>

      {isOpen && (
        <div className={`absolute top-full ${
          compact ? 'left-0 w-64' : 'left-0 w-full'
        } z-50 mt-1 border rounded-lg shadow-xl ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
        }`}>
          <div className={`p-2 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <input
              type="text"
              placeholder="🔍 Hledat uživatele..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className={`w-full px-2 py-1.5 text-sm border rounded ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
          </div>
          <div className={`${compact ? 'max-h-[70vh]' : 'h-[calc(100vh-220px)]'} overflow-y-auto`}>
            {!search && specialOptions.length > 0 && (
              <>
                {specialOptions.map(opt => (
                  <button
                    key={opt.value || '_empty'}
                    type="button"
                    onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      value === opt.value
                        ? isDarkMode ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-900'
                        : isDarkMode ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
                <div className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`} />
              </>
            )}
            {!search && specialOptions.length === 0 && (
              <button
                type="button"
                onClick={() => { onChange(''); setIsOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm ${
                  isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                -- Vyberte uživatele --
              </button>
            )}
            {groups.map(({ role, label, users: roleUsers }) => (
              <div key={role}>
                <div className={`px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                  isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                }`}>
                  {label} ({roleUsers.length})
                </div>
                {roleUsers.map(user => (
                  <button
                    key={user._id}
                    type="button"
                    onClick={() => { onChange(user._id); setIsOpen(false); setSearch(''); }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      value === user._id
                        ? isDarkMode ? 'bg-blue-900/50 text-blue-200 font-semibold' : 'bg-blue-100 text-blue-900 font-semibold'
                        : isDarkMode ? 'text-white hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {user.name || user.username}
                  </button>
                ))}
              </div>
            ))}
            {search && groups.length === 0 && (
              <div className={`px-3 py-3 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Žádný uživatel nenalezen
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
// ====== Konec module-level helpers ======

export default function ContactManagement({ isDarkMode, userRole, currentUserId, fetchCompanies, companies }) {
  const navigate = useNavigate();
  
  // Role permissions
  const { permissions, hasPermission } = useRolePermissions(userRole);
  
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0, status: '' });
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [batchGroups, setBatchGroups] = useState({});
  const [contactsUser, setContactsUser] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [showBatches, setShowBatches] = useState(true); // Toggle pro zobrazení šarží
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    ico: true,
    address: true,
    kraj: false,
    operator: true,
    status: true,
    phone: true,
    email: true,
    contact: true,
    bmsl: false,
    forecast: false,
    ca: false,
    executive: true,
    prm: false,
    closedDate: false,
    contractLength: false,
    personType: false,
    nextActionDate: true,
    owner: false,
    icoStatus: false,
    signal: false
  });
  
  // Pořadí sloupců (z leva doprava)
  const [columnOrder, setColumnOrder] = useState([
    'name', 'ico', 'address', 'kraj', 'nextActionDate', 'operator', 'status', 'contact', 'phone', 'email', 'executive', 'owner', 'bmsl', 'forecast', 'ca', 'prm', 'closedDate', 'contractLength', 'personType', 'icoStatus', 'signal'
  ]);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [showBatchTransfer, setShowBatchTransfer] = useState(false);
  const [targetBatch, setTargetBatch] = useState('');
  const [selectedTransferUser, setSelectedTransferUser] = useState('');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectorSearch, setSelectorSearch] = useState('');
  const [newBatchName, setNewBatchName] = useState('');
  const [editingBatchName, setEditingBatchName] = useState(null);
  const [newBatchNameForRename, setNewBatchNameForRename] = useState('');
  const [batchPagination, setBatchPagination] = useState({});
  const [contactsPerPage, setContactsPerPage] = useState(1000);
  
  // Optimální chunk size na základě uživatelského nastavení
  const getOptimalChunkSize = (currentPage, totalEstimate = 0) => {
    // Pokud je contactsPerPage nekonečno (všechno), použij maximální chunk
    if (contactsPerPage === Infinity) return 100;
    
    // Pro velká nastavení (500+) nebo výchozí hodnoty, načítej maximálním chunku pro rychlost
    if (contactsPerPage >= 500) return 100;
    
    // Pro menší nastavení načítej po menších chunkcích pro rychlejší zobrazení první stránky
    return Math.min(contactsPerPage, 100);
  };
  
  // Funkce pro zrušení načítání
  const cancelLoading = () => {
    console.log('🚫 Uživatel zrušil načítání');
    setLoadingCancelled(true);
  };
  
  // Inline editace operátorů a PRM
  const [editingOperator, setEditingOperator] = useState(null);
  const [editingPRM, setEditingPRM] = useState(null);
  
  // ImportWizard a OperatorVerification
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importUpdateMode, setImportUpdateMode] = useState(false);
  const [showOperatorVerification, setShowOperatorVerification] = useState(false);
  const [showCAVerification, setShowCAVerification] = useState(false);
  const [showSimpleCAImport, setShowSimpleCAImport] = useState(false);
  const [showMsisdnIcoFix, setShowMsisdnIcoFix] = useState(false);
  const [showBulkExport, setShowBulkExport] = useState(false);
  const [showIcoOwnerChecker, setShowIcoOwnerChecker] = useState(false);
  const [showBulkContactImport, setShowBulkContactImport] = useState(false);

  // Hromadné ověření signálu
  const [isBulkSignal, setIsBulkSignal] = useState(false);
  const [bulkSignalProgress, setBulkSignalProgress] = useState({ current: 0, total: 0, status: '' });

  // Hodnocení šarží – struktura: { batchName: { userId: { stars, userName } } }
  const [showRatings, setShowRatings] = useState(false);
  const [expandedRatingBatch, setExpandedRatingBatch] = useState(null);
  const [batchRatings, setBatchRatings] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('batchRatings') || '{}');
      // Migrace starého formátu { batchName: number } → nový formát
      const migrated = {};
      for (const [k, v] of Object.entries(raw)) {
        migrated[k] = (typeof v === 'object' && v !== null && !Array.isArray(v)) ? v : {};
      }
      return migrated;
    } catch { return {}; }
  });
  const setRating = (batchName, stars) => {
    const currentUserName = users.find(u => u._id === currentUserId)?.username || 'Já';
    const batchEntry = { ...(batchRatings[batchName] || {}) };
    if (stars === 0) {
      delete batchEntry[currentUserId];
    } else {
      batchEntry[currentUserId] = { stars, userName: currentUserName };
    }
    const updated = { ...batchRatings, [batchName]: batchEntry };
    setBatchRatings(updated);
    localStorage.setItem('batchRatings', JSON.stringify(updated));
  };

  const deleteUserRating = (batchName, uid) => {
    const batchEntry = { ...(batchRatings[batchName] || {}) };
    delete batchEntry[uid];
    const updated = { ...batchRatings, [batchName]: batchEntry };
    setBatchRatings(updated);
    localStorage.setItem('batchRatings', JSON.stringify(updated));
  };
  
  // Drag & drop state pro nastavení sloupců
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Uložené filtry
  const [showSavedFilters, setShowSavedFilters] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  
  // Čištění kontaktů (dočasná funkce)
  const [showContactCleaner, setShowContactCleaner] = useState(false);
  const [contactCleanPreview, setContactCleanPreview] = useState([]);
  const [isCleaningContacts, setIsCleaningContacts] = useState(false);
  const [cleaningProgress, setCleaningProgress] = useState({ current: 0, total: 0, status: '' });

  // Otočení datumů (dočasná funkce)
  const [showDateFlipper, setShowDateFlipper] = useState(false);
  const [dateFlipPreview, setDateFlipPreview] = useState([]);
  const [isFlippingDates, setIsFlippingDates] = useState(false);
  const [dateFlipProgress, setDateFlipProgress] = useState({ current: 0, total: 0, status: '' });

  // Oprava telefonů (dočasná funkce)
  const [showPhoneFixer, setShowPhoneFixer] = useState(false);
  const [phoneFixPreview, setPhoneFixPreview] = useState([]);
  const [isFixingPhones, setIsFixingPhones] = useState(false);
  const [phoneFixProgress, setPhoneFixProgress] = useState({ current: 0, total: 0, status: '' });

  // Aktualizace adres z ARES
  const [isUpdatingAddresses, setIsUpdatingAddresses] = useState(false);
  const [addressUpdateProgress, setAddressUpdateProgress] = useState({ current: 0, total: 0, status: '' });

  // Automatické ověření na pozadí
  const [showAutoVerification, setShowAutoVerification] = useState(false);

  // Vrácení DTB
  const [showVraceniDtb, setShowVraceniDtb] = useState(false);
  
  // Stav pro zrušení načítání
  const [loadingCancelled, setLoadingCancelled] = useState(false);
  
  // Filtry pro jednotlivé sloupce
  const [columnFilters, setColumnFilters] = useState(() => {
    try {
      const uid = localStorage.getItem('userId');
      if (uid) {
        const saved = localStorage.getItem(`contactManagement_filters_${uid}`);
        if (saved) return { ...DEFAULT_COLUMN_FILTERS, ...JSON.parse(saved) };
      }
    } catch {}
    return { ...DEFAULT_COLUMN_FILTERS };
  });

  // Debounced verze filtrů – skutečné filtrování se spustí 300 ms po skončení psaní
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState(() => {
    try {
      const uid = localStorage.getItem('userId');
      if (uid) {
        const saved = localStorage.getItem(`contactManagement_filters_${uid}`);
        if (saved) return { ...DEFAULT_COLUMN_FILTERS, ...JSON.parse(saved) };
      }
    } catch {}
    return { ...DEFAULT_COLUMN_FILTERS };
  });
  const filterDebounceRef = useRef(null);

  // State pro řazení sloupců
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: null // 'asc' nebo 'desc'
  });

  // Globální vyhledávač
  // Globální vyhledavač
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isGlobalSearching, setIsGlobalSearching] = useState(false);

  // Načíst uživatele a kontakty pro uživatele s rolí "contacts"
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Načíst preference ContactManagement ze serveru
        let serverPrefs = {};
        if (currentUserId) {
          try {
            const prefsRes = await fetch(`${API_URL}/users/${currentUserId}/cm-preferences`);
            if (prefsRes.ok) {
              const prefsData = await prefsRes.json();
              serverPrefs = prefsData.cmPreferences || {};
            }
          } catch (e) {
            console.warn('Nepodařilo se načíst CM preference ze serveru, používám localStorage:', e);
          }
        }

        // Načíst nastavení sloupců (server má přednost před localStorage)
        const rawColumns =
          serverPrefs.columns ||
          (() => { try { return JSON.parse(localStorage.getItem(`contactManagement_columns_${currentUserId}`)); } catch { return null; } })();
        if (rawColumns) {
          rawColumns.nextActionDate = true; // vždy viditelné
          setVisibleColumns(prev => ({ ...prev, ...rawColumns }));
        } else {
          setVisibleColumns(prev => ({ ...prev, nextActionDate: true }));
        }

        // Načíst aktivní filtry (server má přednost před localStorage)
        const rawFilters =
          serverPrefs.filters ||
          (() => { try { return JSON.parse(localStorage.getItem(`contactManagement_filters_${currentUserId}`)); } catch { return null; } })();
        if (rawFilters) {
          const mergedFilters = { ...DEFAULT_COLUMN_FILTERS, ...rawFilters };
          setColumnFilters(mergedFilters);
          setDebouncedColumnFilters(mergedFilters);
        }

        // Načíst pořadí sloupců (server má přednost před localStorage)
        const rawOrder =
          serverPrefs.columnOrder ||
          (() => { try { return JSON.parse(localStorage.getItem(`contactManagement_columnOrder_${currentUserId}`)); } catch { return null; } })();
        if (rawOrder) {
          // Přidat nové sloupce, které nejsou v uloženém pořadí (zpětná kompatibilita)
          const defaultOrder = ['name', 'ico', 'address', 'kraj', 'nextActionDate', 'operator', 'status', 'contact', 'phone', 'email', 'executive', 'owner', 'bmsl', 'forecast', 'ca', 'prm', 'closedDate', 'contractLength', 'personType', 'icoStatus', 'signal'];
          const merged = [...rawOrder, ...defaultOrder.filter(col => !rawOrder.includes(col))];
          setColumnOrder(merged);
        }

        // Načíst pojmenované filtry (server má přednost před localStorage)
        const rawSavedFilters =
          serverPrefs.savedFilters ||
          (() => { try { return JSON.parse(localStorage.getItem(`contactManagement_savedFilters_${currentUserId}`)); } catch { return null; } })();
        if (Array.isArray(rawSavedFilters)) setSavedFilters(rawSavedFilters);

        
        // Načíst seznam všech uživatelů
        const usersRes = await fetch(`${API_URL}/users?userId=${currentUserId || ''}`);
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          
          // Filtruj uživatele podle role permissions
          const visibleUsers = hasPermission('viewAllUsers') 
            ? usersData 
            : usersData.filter(user => user._id === currentUserId);
          
          setUsers(visibleUsers);
          
          // Najít uživatele s rolí "contacts"
          const contactsUserData = usersData.find(user => user.role === 'contacts');
          setContactsUser(contactsUserData);
          
          // Najít uživatele "databáze" a připravit paginaci
          const databaseUser = visibleUsers.find(u => 
            u.role === 'database' || 
            u.name?.toLowerCase().includes('databáze') ||
            u.username?.toLowerCase().includes('database')
          );
          
          if (databaseUser) {
            console.log('Nalezen uživatel databáze, připravena paginace:', databaseUser.name || databaseUser.username);
          }
          
          // Nenastavovat automaticky uživatele - nechej uživatele vybrat
        }
      } catch (error) {
        console.error('Chyba při načítání dat:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUserId]); // Pouze currentUserId, ne hasPermission

  // useEffect pro přenačtení dat při změně vybraného uživatele
  useEffect(() => {
    if (selectedUser && selectedUsers.length === 0) {
      loadUserCompaniesAuto(selectedUser, 1);
    }
    // contactsPerPage záměrně není v dependencies - ovlivňuje jen zobrazení, ne načítání dat
    // Odstranění automatického načítání při změně selectedUsers - načítá se pouze po potvrzení
  }, [selectedUser, users]);

  // Helper pro ukládání CM preferencí na server (+ localStorage jako cache)
  const saveCMPref = useCallback((key, value) => {
    // LocalStorage cache
    localStorage.setItem(`contactManagement_${key}_${currentUserId}`, JSON.stringify(value));
    // Server (fire-and-forget, chyba nevadí – data jsou v localStorage)
    if (currentUserId) {
      fetch(`${API_URL}/users/${currentUserId}/cm-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value }),
      }).catch(err => console.warn('CM prefs save failed:', err));
    }
  }, [currentUserId]);

  // Funkce pro aktualizaci filtrů sloupců
  const updateColumnFilter = (column, value) => {
    const newFilters = {
      ...columnFilters,
      [column]: value
    };
    setColumnFilters(newFilters);

    // Uložení filtrů (server + localStorage cache)
    saveCMPref('filters', newFilters);

    // Debounce: aktualizuj filtry pro výpočty až po 300 ms od poslední změny
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      setDebouncedColumnFilters(newFilters);
    }, 300);
  };

  // Pomocná funkce pro automatické načtení uživatelových společností se správnou paginací
  const loadUserCompaniesAuto = (userId, page = 1) => {
    if (!userId) return Promise.resolve();
    
    // Určit zda použít paginaci na základě jména/role uživatele
    const isDatabaseUser = users.find(u => u._id === userId)?.role === 'database' || 
                           users.find(u => u._id === userId)?.name?.toLowerCase().includes('databáze') ||
                           users.find(u => u._id === userId)?.username?.toLowerCase().includes('database');
    
    // Pro uživatele "databáze" použij paginaci, pro ostatní loadAll=true
    return loadUserCompanies(userId, page, !isDatabaseUser);
  };

  // Funkce pro vymazání všech filtrů
  const clearAllFilters = () => {
    const emptyFilters = {
      name: '',
      ico: '',
      address: '',
      operator: '',
      status: '',
      contact: '',
      phone: '',
      email: '',
      bmsl: '',
      forecast: '',
      ca: '',
      executive: '',
      prm: '',
      closedDate: '',
      contractLength: '',
      personType: '',
      nextActionDate: '',
      owner: ''
    };
    setColumnFilters(emptyFilters);
    setDebouncedColumnFilters(emptyFilters); // ihned, bez debounce
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    
    saveCMPref('filters', emptyFilters);
  };

  // Funkce pro inline editaci operátora
  const handleOperatorChange = async (contactId, newOperator) => {
    try {
      const response = await fetch(`${API_URL}/companies/${contactId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ operator: newOperator }),
      });

      if (response.ok) {
        // Aktualizuj lokální data
        setContacts(prevContacts => 
          prevContacts.map(contact => 
            contact._id === contactId 
              ? { ...contact, operator: newOperator }
              : contact
          )
        );
        
        // Aktualizuj batchGroups
        setBatchGroups(prevGroups => {
          const newGroups = { ...prevGroups };
          Object.keys(newGroups).forEach(batchName => {
            newGroups[batchName] = newGroups[batchName].map(contact =>
              contact._id === contactId 
                ? { ...contact, operator: newOperator }
                : contact
            );
          });
          return newGroups;
        });
        
        setEditingOperator(null);
      } else {
        window.alert('Chyba při ukládání operátora');
      }
    } catch (error) {
      console.error('Chyba při aktualizaci operátora:', error);
      window.alert('Chyba při ukládání operátora');
    }
  };

  // Funkce pro inline editaci PRM
  const handlePRMChange = async (contactId, newPRM) => {
    try {
      const response = await fetch(`${API_URL}/companies/${contactId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prm: newPRM }),
      });

      if (response.ok) {
        // Aktualizuj lokální data
        setContacts(prevContacts => 
          prevContacts.map(contact => 
            contact._id === contactId 
              ? { ...contact, prm: newPRM }
              : contact
          )
        );
        
        // Aktualizuj batchGroups
        setBatchGroups(prevGroups => {
          const newGroups = { ...prevGroups };
          Object.keys(newGroups).forEach(batchName => {
            newGroups[batchName] = newGroups[batchName].map(contact =>
              contact._id === contactId 
                ? { ...contact, prm: newPRM }
                : contact
            );
          });
          return newGroups;
        });
        
        setEditingPRM(null);
      } else {
        window.alert('Chyba při ukládání PRM Vodafone');
      }
    } catch (error) {
      console.error('Chyba při aktualizaci PRM:', error);
      window.alert('Chyba při ukládání PRM Vodafone');
    }
  };

  // Funkce pro změnu pořadí sloupců
  const moveColumn = (columnName, direction) => {
    const currentIndex = columnOrder.indexOf(columnName);
    if (currentIndex === -1) return;
    
    let newIndex;
    if (direction === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < columnOrder.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return; // Žádná změna
    }
    
    const newOrder = [...columnOrder];
    // Prohoď pozice
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    setColumnOrder(newOrder);
    
    saveCMPref('columnOrder', newOrder);
  };

  // Drag & Drop funkce pro sloupce
  const handleDragStart = (e, columnName, index) => {
    setDraggedColumn({ name: columnName, index: index });
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    setDraggedColumn(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    // Pouze pokud opouštíme element kompletně
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    
    if (!draggedColumn || draggedColumn.index === targetIndex) {
      return;
    }

    const newOrder = [...columnOrder];
    const draggedItem = newOrder.splice(draggedColumn.index, 1)[0];
    newOrder.splice(targetIndex, 0, draggedItem);
    
    setColumnOrder(newOrder);
    setDragOverIndex(null);
    
    saveCMPref('columnOrder', newOrder);
  };

  // Funkce pro práci s uloženými filtry
  const loadSavedFilters = () => {
    const saved = localStorage.getItem(`contactManagement_savedFilters_${currentUserId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSavedFilters(parsed);
      } catch (e) {
        console.error('Chyba při načítání uložených filtrů:', e);
        setSavedFilters([]);
      }
    } else {
      setSavedFilters([]);
    }
  };

  const saveCurrentFilters = () => {
    // Ensure all default keys are present before saving (handles old/partial state)
    const filtersToSave = { ...DEFAULT_COLUMN_FILTERS, ...columnFilters };
    const hasActiveFilters = Object.values(filtersToSave).some(filter => (filter || '').trim() !== '');
    
    if (!hasActiveFilters) {
      window.alert('Žádné filtry nejsou nastaveny');
      return;
    }

    const filterName = window.prompt('Zadejte název pro uložené filtry:');
    if (!filterName || filterName.trim() === '') {
      return;
    }

    const newFilter = {
      id: Date.now(),
      name: filterName.trim(),
      filters: filtersToSave,
      createdAt: new Date().toISOString()
    };

    const updatedSavedFilters = [...savedFilters, newFilter];
    setSavedFilters(updatedSavedFilters);
    saveCMPref('savedFilters', updatedSavedFilters);

    window.alert('Filtry byly úspěšně uloženy');
  };

  const loadFilterSet = (filterSet) => {
    // Merge with defaults to ensure all keys exist (handles filters saved in older versions)
    const mergedFilters = { ...DEFAULT_COLUMN_FILTERS, ...filterSet.filters };
    setColumnFilters(mergedFilters);
    setDebouncedColumnFilters(mergedFilters); // ihned, bez debounce
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    saveCMPref('filters', mergedFilters);
    setShowSavedFilters(false);
  };

  const deleteSavedFilter = (filterId) => {
    if (window.confirm('Opravdu chcete smazat tento uložený filtr?')) {
      const updatedFilters = savedFilters.filter(f => f.id !== filterId);
      setSavedFilters(updatedFilters);
      saveCMPref('savedFilters', updatedFilters);
    }
  };

  // Funkce pro řazení sloupců
  const handleSort = (columnName) => {
    let direction = 'asc';
    if (sortConfig.column === columnName && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ column: columnName, direction });
  };

  // Funkce pro aplikování řazení na kontakty
  const sortContacts = (contactsList) => {
    if (!sortConfig.column || !sortConfig.direction) {
      return contactsList;
    }

    const sorted = [...contactsList].sort((a, b) => {
      let aValue = '';
      let bValue = '';

      // Získání hodnot podle sloupce
      switch (sortConfig.column) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'ico':
          aValue = a.ico || '';
          bValue = b.ico || '';
          break;
        case 'address':
          aValue = a.address || '';
          bValue = b.address || '';
          break;
        case 'kraj':
          aValue = a.kraj || '';
          bValue = b.kraj || '';
          break;
        case 'operator':
          aValue = a.operator || '';
          bValue = b.operator || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'contact':
          aValue = a.contacts && a.contacts[0] ? a.contacts[0].name || '' : '';
          bValue = b.contacts && b.contacts[0] ? b.contacts[0].name || '' : '';
          break;
        case 'phone':
          aValue = a.contacts && a.contacts[0] ? a.contacts[0].phone || '' : '';
          bValue = b.contacts && b.contacts[0] ? b.contacts[0].phone || '' : '';
          break;
        case 'email':
          aValue = a.contacts && a.contacts[0] ? a.contacts[0].email || '' : '';
          bValue = b.contacts && b.contacts[0] ? b.contacts[0].email || '' : '';
          break;
        case 'bmsl':
          // Numerické řazení pro BMSL
          aValue = parseFloat(a.bmsl) || 0;
          bValue = parseFloat(b.bmsl) || 0;
          break;
        case 'forecast':
          // Numerické řazení pro Forecast
          aValue = parseFloat(a.forecast) || 0;
          bValue = parseFloat(b.forecast) || 0;
          break;
        case 'ca':
          aValue = a.ca || '';
          bValue = b.ca || '';
          break;
        case 'executive':
          // Použít createdBy jméno pro řazení
          aValue = '';
          bValue = '';
          if (typeof a.createdBy === 'object' && a.createdBy?.name) {
            aValue = a.createdBy.name;
          } else if (typeof a.createdBy === 'string') {
            aValue = a.ownerInfo?.username || '';
          }
          if (typeof b.createdBy === 'object' && b.createdBy?.name) {
            bValue = b.createdBy.name;
          } else if (typeof b.createdBy === 'string') {
            bValue = b.ownerInfo?.username || '';
          }
          break;
        case 'prm':
          aValue = a.prm || '';
          bValue = b.prm || '';
          break;
        case 'closedDate':
          aValue = a.closedDate ? new Date(a.closedDate) : new Date(0);
          bValue = b.closedDate ? new Date(b.closedDate) : new Date(0);
          break;
        case 'contractLength':
          // Numerické řazení pro délku smlouvy
          aValue = parseInt(a.contractLength) || 0;
          bValue = parseInt(b.contractLength) || 0;
          break;
        case 'personType':
          aValue = a.personType || '';
          bValue = b.personType || '';
          break;
        case 'icoStatus':
          aValue = a.icoStatus || '';
          bValue = b.icoStatus || '';
          break;
        case 'signal':
          aValue = a.signal?.bars ?? -1;
          bValue = b.signal?.bars ?? -1;
          break;
        case 'nextActionDate':
          aValue = a.nextActionDate ? new Date(a.nextActionDate) : new Date(0);
          bValue = b.nextActionDate ? new Date(b.nextActionDate) : new Date(0);
          break;
        case 'owner':
          aValue = a.ownerInfo?.username || '';
          bValue = b.ownerInfo?.username || '';
          break;
        default:
          return 0;
      }

      // Porovnání hodnot
      let result = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        result = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        result = aValue.getTime() - bValue.getTime();
      } else {
        // Textové porovnání
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
        if (aValue < bValue) result = -1;
        else if (aValue > bValue) result = 1;
        else result = 0;
      }

      return sortConfig.direction === 'asc' ? result : -result;
    });

    return sorted;
  };

  // Funkce pro aplikaci filtrů na kontakty (optimalizovaná – předkompiluje podmínky před iterací 250k záznamů)
  const applyFiltersToContacts = useCallback((contactsList) => {
    // --- Pre-kompilace filtrů (proběhne jednou, ne pro každý kontakt) ---
    const numericPatterns = [
      { regex: /^>=(.+)$/, op: '>=' },
      { regex: /^<=(.+)$/, op: '<=' },
      { regex: /^>(.+)$/,  op: '>' },
      { regex: /^<(.+)$/,  op: '<' },
      { regex: /^=(.+)$/,  op: '=' },
    ];
    const emptyTerms = ['n/a', 'doplň', 'dopl', 'prázdné', 'prazdne'];

    const compile = (raw) => {
      if (!raw || raw.trim() === '') return null;
      const fv = raw.trim();
      const fvl = fv.toLowerCase();
      if (emptyTerms.some(t => fvl.includes(t))) return { type: 'empty' };
      if (fv.includes(',')) {
        return { type: 'multi', vals: fv.split(',').map(v => v.trim().toLowerCase()).filter(Boolean) };
      }
      for (const { regex, op } of numericPatterns) {
        const m = fv.match(regex);
        if (m) return { type: 'num', op, n: parseFloat(m[1]) };
      }
      const rm = fv.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
      if (rm) return { type: 'range', lo: parseFloat(rm[1]), hi: parseFloat(rm[2]) };
      return { type: 'str', lv: fvl };
    };

    const check = (fieldValue, c) => {
      if (!c) return true;
      const s = fieldValue != null ? String(fieldValue) : '';
      switch (c.type) {
        case 'empty': return s.trim() === '';
        case 'multi': { const sl = s.toLowerCase(); return c.vals.some(v => sl.includes(v)); }
        case 'num':   { const fn = parseFloat(s); if (isNaN(c.n) || isNaN(fn)) return false;
                        switch (c.op) {
                          case '>':  return fn > c.n;
                          case '>=': return fn >= c.n;
                          case '<':  return fn < c.n;
                          case '<=': return fn <= c.n;
                          case '=':  return fn === c.n;
                          default:   return false;
                        } }
        case 'range': { const fn = parseFloat(s); return !isNaN(fn) && fn >= c.lo && fn <= c.hi; }
        case 'str':   return s.toLowerCase().includes(c.lv);
        default:      return true;
      }
    };

    const cf = {
      name:           compile(debouncedColumnFilters.name),
      ico:            compile(debouncedColumnFilters.ico),
      address:        compile(debouncedColumnFilters.address),
      kraj:           compile(debouncedColumnFilters.kraj),
      operator:       compile(debouncedColumnFilters.operator),
      status:         compile(debouncedColumnFilters.status),
      contact:        compile(debouncedColumnFilters.contact),
      phone:          compile(debouncedColumnFilters.phone),
      email:          compile(debouncedColumnFilters.email),
      bmsl:           compile(debouncedColumnFilters.bmsl),
      forecast:       compile(debouncedColumnFilters.forecast),
      ca:             compile(debouncedColumnFilters.ca),
      executive:      compile(debouncedColumnFilters.executive),
      prm:            compile(debouncedColumnFilters.prm),
      closedDate:     compile(debouncedColumnFilters.closedDate),
      contractLength: compile(debouncedColumnFilters.contractLength),
      personType:     compile(debouncedColumnFilters.personType),
      nextActionDate: compile(debouncedColumnFilters.nextActionDate),
      owner:          compile(debouncedColumnFilters.owner),
      signal:         compile(debouncedColumnFilters.signal),
    };

    // Rychlá cesta: žádné aktivní filtry → vrátíme seznam beze změny
    if (!Object.values(cf).some(Boolean)) return contactsList;

    // Pomocná funkce – zachována pro zpětnou kompatibilitu s logikou níže
    const matchesFilter = (fieldValue, filterValue) => {
      const compiled = compile(filterValue);
      return check(fieldValue, compiled);
    };

    // --- Iterace přes kontakty (používá předkompilované cf, ne recompile) ---
    return contactsList.filter(contact => {
      if (!check(contact.name, cf.name)) return false;
      if (!check(contact.ico, cf.ico)) return false;
      if (!check(contact.address, cf.address)) return false;
      if (!check(contact.kraj, cf.kraj)) return false;
      if (!check(contact.operator, cf.operator)) return false;
      if (!check(contact.status, cf.status)) return false;
      if (cf.contact && !check(contact.contacts?.[0]?.name || '', cf.contact)) return false;
      if (cf.phone   && !check(contact.contacts?.[0]?.phone || '', cf.phone)) return false;
      if (cf.email   && !check(contact.contacts?.[0]?.email || '', cf.email)) return false;
      if (!check(contact.bmsl, cf.bmsl)) return false;
      if (!check(contact.forecast, cf.forecast)) return false;
      if (!check(contact.ca, cf.ca)) return false;
      // Filtr Vlastník firmy CRM
      let executiveName = '';
      if (typeof contact.createdBy === 'object' && contact.createdBy?.name) {
        executiveName = contact.createdBy.name;
      } else if (typeof contact.createdBy === 'string') {
        executiveName = contact.ownerInfo?.username || '';
      }
      if (!check(executiveName, cf.executive)) return false;
      if (!check(contact.prm, cf.prm)) return false;
      if (!check(contact.closedDate, cf.closedDate)) return false;
      if (!check(contact.contractLength, cf.contractLength)) return false;
      if (!check(contact.personType || detectPersonType(contact.name), cf.personType)) return false;
      if (!check(contact.nextActionDate, cf.nextActionDate)) return false;
      if (!check(contact.ownerInfo?.username, cf.owner)) return false;
      if (!check(contact.signal ? String(contact.signal.bars) : '', cf.signal)) return false;
      return true;
    });
  }, [debouncedColumnFilters]);

  // Memoizované výsledky filtrování – přepočítají se jen při změně dat nebo filtrů
  const filteredContactsAll = useMemo(
    () => applyFiltersToContacts(contacts),
    [contacts, applyFiltersToContacts]
  );

  const filteredBatchGroupsMemo = useMemo(() => {
    const result = {};
    for (const [name, list] of Object.entries(batchGroups)) {
      result[name] = applyFiltersToContacts(list);
    }
    return result;
  }, [batchGroups, applyFiltersToContacts]);

  // Funkce pro vrácení šarže kontaktů zpět do nerozděleného stavu
  const returnBatchToContacts = async (batchName) => {
    if (!contactsUser) return;
    
    try {
      // Najít všechny kontakty v této šarži
      const batchContacts = batchGroups[batchName] || [];
      if (batchContacts.length === 0) return;

      const contactIds = batchContacts.map(contact => contact._id);

      const response = await fetch(`${API_URL}/batches/return-contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contactIds: contactIds,
          contactsUserId: contactsUser._id
        }),
      });

      if (response.ok) {
        window.alert(`Šarže "${batchName}" byla úspěšně vrácena zpět k rozdělení.`);
        // Obnovit data
        window.location.reload();
      } else {
        const errorData = await response.json();
        window.alert('Chyba při vrácení šarže: ' + (errorData.message || 'Neznámá chyba'));
      }
    } catch (error) {
      console.error('Chyba při vrácení šarže:', error);
      window.alert('Chyba při vrácení šarže: ' + error.message);
    }
  };

  // Funkce pro rozbalení/sbalení šarže
  const toggleBatchExpansion = (batchName) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchName)) {
      newExpanded.delete(batchName);
    } else {
      newExpanded.add(batchName);
    }
    setExpandedBatches(newExpanded);
  };

  // Funkce pro přepínání viditelnosti sloupců
  const toggleColumn = (columnName) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnName]: !visibleColumns[columnName]
    };
    setVisibleColumns(newVisibleColumns);
    
    // Uložení vizibilních sloupců
    saveCMPref('columns', newVisibleColumns);
  };

  // Funkce pro označení/odznačení kontaktu
  const toggleContactSelection = (contactId) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
  };

  // Funkce pro označení všech kontaktů v šarži (jen na aktuální stránce)
  const toggleBatchSelection = (batchName) => {
    const batchContacts = batchGroups[batchName] || [];
    const paginatedContacts = getPaginatedContacts(batchContacts, batchName);
    const paginatedContactIds = paginatedContacts.map(c => c._id);
    const newSelected = new Set(selectedContacts);
    
    // Pokud jsou všechny kontakty na aktuální stránce označené, odznač je
    const allSelected = paginatedContactIds.every(id => newSelected.has(id));
    if (allSelected) {
      paginatedContactIds.forEach(id => newSelected.delete(id));
    } else {
      // Jinak označ všechny na aktuální stránce
      paginatedContactIds.forEach(id => newSelected.add(id));
    }
    setSelectedContacts(newSelected);
  };

  // Označit / odznačit CELOU šarži (všechny stránky)
  const selectAllInBatch = (batchName) => {
    const allIds = (batchGroups[batchName] || []).map(c => c._id);
    const newSelected = new Set(selectedContacts);
    const allAlreadySelected = allIds.length > 0 && allIds.every(id => newSelected.has(id));
    if (allAlreadySelected) {
      allIds.forEach(id => newSelected.delete(id));
    } else {
      allIds.forEach(id => newSelected.add(id));
    }
    setSelectedContacts(newSelected);
  };

  // Funkce pro převod označených kontaktů na jiného uživatele
  const transferSelectedContacts = async (targetUserId) => {
    if (selectedContacts.size === 0) {
      window.alert('Nejdříve označte kontakty, které chcete převést.');
      return;
    }

    if (!targetUserId) {
      window.alert('Vyberte cílového uživatele.');
      return;
    }

    const toUser = users.find(u => u._id === targetUserId);
    if (!toUser) {
      window.alert('Vybraný uživatel neexistuje.');
      return;
    }

    // Kontrola, zda cílový uživatel není mezi vybranými (jednotlivý nebo více)
    if (selectedUser && toUser._id === selectedUser) {
      window.alert('Nelze převést kontakty na stejného uživatele.');
      return;
    }
    
    if (selectedUsers.length > 0 && selectedUsers.includes(toUser._id)) {
      window.alert('Nelze převést kontakty na některého z vybraných uživatelů.');
      return;
    }

    const confirmTransfer = window.confirm(
      `Opravdu chcete převést ${selectedContacts.size} označených kontaktů na ${toUser.username}?`
    );

    if (!confirmTransfer) return;

    const currentUserId = localStorage.getItem("userId");
    const transferredCount = selectedContacts.size;

    try {
      setLoading(true);
      setLoadingProgress({ current: 0, total: transferredCount, status: 'Přesouvám kontakty...', isTransfer: true, eta: null });

      const res = await fetch(`${API_URL}/companies/bulk-transfer-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': currentUserId },
        body: JSON.stringify({
          companyIds: [...selectedContacts],
          targetUserId: toUser._id,
          initiatedBy: currentUserId,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Chyba při hromadném přesunu');
      }

      setSelectedContacts(new Set());
      setLoadingProgress({ current: 0, total: 0, status: 'Načítám kontakty...' });

      // Obnovit data – await zajistí správný loading stav
      if (selectedUser && selectedUsers.length === 0) {
        await loadUserCompaniesAuto(selectedUser);
      } else if (selectedUsers.length > 0) {
        await loadSelectedUsersCompanies();
      }

      if (fetchCompanies) fetchCompanies();
      window.alert(`Úspěšně převedeno ${transferredCount} kontaktů na ${toUser.username}!`);
    } catch (error) {
      console.error('Chyba při převodu kontaktů:', error);
      window.alert('Chyba při převodu kontaktů: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingProgress({ current: 0, total: 0, status: '' });
    }
  };

  // Funkce pro přejmenování šarže
  // Export telefonních čísel pro konkrétní šarži
  const exportPhoneNumbers = (batchName) => {
    const batchContacts = batchGroups[batchName] || [];
    
    // Sestaví seznam telefonních čísel
    const phoneNumbers = [];
    batchContacts.forEach(contact => {
      if (contact.contacts && Array.isArray(contact.contacts)) {
        contact.contacts.forEach(contactInfo => {
          if (contactInfo.phone && contactInfo.phone.trim()) {
            phoneNumbers.push(contactInfo.phone.trim());
          }
        });
      }
    });
    
    if (phoneNumbers.length === 0) {
      alert('V této šarži nejsou žádná telefonní čísla k exportu.');
      return;
    }
    
    // Vytvoří TXT obsah - pouze telefonní čísla, každé na novém řádku
    const txtContent = phoneNumbers.join('\n');
    
    // Stáhne jako TXT soubor
    const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `telefony-${batchName.replace(/[^\w\s-]/g, '_')}-${new Date().toISOString().split('T')[0]}.txt`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    alert(`Exportováno ${phoneNumbers.length} telefonních čísel ze šarže "${batchName}".`);
  };

  // Callback po úspěšném importu
  const handleImportComplete = () => {
    // Znovu načti data pro aktuálního uživatele
    if (selectedUser && selectedUsers.length === 0) {
      loadUserCompaniesAuto(selectedUser);
    } else if (selectedUsers.length > 0) {
      loadSelectedUsersCompanies();
    }
    
    // Aktualizovat hlavní seznam firem v App.js
    if (fetchCompanies) {
      fetchCompanies();
    }
    
    setShowImportWizard(false);
    setImportUpdateMode(false);
  };

  const renameBatch = async (oldBatchName, newBatchName) => {
    if (!newBatchName.trim()) {
      window.alert('Zadejte nový název šarže.');
      return;
    }

    if (newBatchName.trim() === oldBatchName) {
      setEditingBatchName(null);
      setNewBatchNameForRename('');
      return;
    }

    if (oldBatchName === 'Bez šarže') {
      window.alert('Tuto šarži nelze přejmenovat.');
      return;
    }

    // Zkontrolovat, zda nový název již neexistuje
    if (Object.keys(batchGroups).some(name => name.toLowerCase() === newBatchName.trim().toLowerCase())) {
      window.alert('Šarže s tímto názvem již existuje.');
      return;
    }

    const batchContacts = batchGroups[oldBatchName] || [];
    if (batchContacts.length === 0) {
      window.alert('Šarže je prázdná.');
      return;
    }

    const confirmRename = window.confirm(
      `Opravdu chcete přejmenovat šarži "${oldBatchName}" na "${newBatchName.trim()}"?`
    );

    if (!confirmRename) return;

    try {
      setLoading(true);
      
      // Přejmenovat všechny kontakty v šarži
      for (const contact of batchContacts) {
        await fetch(`${API_URL}/companies/${contact._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...contact,
            batchName: newBatchName.trim(),
          }),
        });
      }
      
      window.alert(`Šarže "${oldBatchName}" byla úspěšně přejmenována na "${newBatchName.trim()}".`);
      setEditingBatchName(null);
      setNewBatchNameForRename('');
      
      // Obnovit data
      if (selectedUser && selectedUsers.length === 0) {
        loadUserCompaniesAuto(selectedUser);
      } else if (selectedUsers.length > 0) {
        loadSelectedUsersCompanies();
      }
      
      // Aktualizovat hlavní seznam firem v App.js
      if (fetchCompanies) {
        fetchCompanies();
      }
    } catch (error) {
      console.error('Chyba při přejmenování šarže:', error);
      window.alert('Chyba při přejmenování šarže: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Funkce pro získání aktuální stránky pro šarži
  const getBatchPage = (batchName) => {
    return batchPagination[batchName] || 1;
  };

  // Funkce pro nastavení stránky pro šarži
  const setBatchPage = (batchName, page) => {
    setBatchPagination(prev => ({
      ...prev,
      [batchName]: page
    }));
  };

  // Funkce pro získání kontaktů pro aktuální stránku s aplikovanými filtry
  const getPaginatedContacts = (batchContacts, batchName) => {
    // Nejdříve aplikuj filtry
    const filteredContacts = applyFiltersToContacts(batchContacts);
    
    // Pak aplikuj řazení
    const sortedContacts = sortContacts(filteredContacts);
    
    const page = getBatchPage(batchName);
    const startIndex = (page - 1) * contactsPerPage;
    const endIndex = startIndex + contactsPerPage;
    return sortedContacts.slice(startIndex, endIndex);
  };

  // Funkce pro výpočet celkového počtu stránek s filtrovanými kontakty
  const getTotalPages = (batchContacts) => {
    // Aplikuj filtry před výpočtem stránek
    const filteredContacts = applyFiltersToContacts(batchContacts);
    return Math.ceil(filteredContacts.length / contactsPerPage);
  };

  // Funkce pro smazání šarže
  const deleteBatch = async (batchName) => {
    if (!batchName || batchName === 'Bez šarže') {
      window.alert('Tuto šarži nelze smazat.');
      return;
    }

    const batchContacts = batchGroups[batchName] || [];
    if (batchContacts.length === 0) {
      window.alert('Šarže je již prázdná.');
      return;
    }

    const confirmDelete = window.confirm(
      `Opravdu chcete smazat šarži "${batchName}"? Všech ${batchContacts.length} kontaktů bude přesunuto do kategorie "Bez šarže".`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);
      
      // Přesunout všechny kontakty do "Bez šarže"
      for (const contact of batchContacts) {
        await fetch(`${API_URL}/companies/${contact._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...contact,
            batchName: null,
          }),
        });
      }
      
      window.alert(`Šarže "${batchName}" byla úspěšně smazána. Kontakty byly přesunuty do kategorie "Bez šarže".`);
      
      // Obnovit data
      if (selectedUser && selectedUsers.length === 0) {
        loadUserCompaniesAuto(selectedUser);
      } else if (selectedUsers.length > 0) {
        loadSelectedUsersCompanies();
      }
      
      // Aktualizovat hlavní seznam firem v App.js
      if (fetchCompanies) {
        fetchCompanies();
      }
    } catch (error) {
      console.error('Chyba při mazání šarže:', error);
      window.alert('Chyba při mazání šarže: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Funkce pro smazání označených kontaktů
  const verifyIcoStatuses = async () => {
    const toVerify = contacts.filter(c => selectedContacts.has(c._id) && c.ico);
    if (toVerify.length === 0) {
      window.alert('Označené firmy nemají vyplněné IČO.');
      return;
    }
    setLoading(true);
    let ok = 0, err = 0;
    for (const company of toVerify) {
      try {
        const cleanIco = company.ico.replace(/\D/g, '').padStart(8, '0');
        const aresRes = await fetch(
          `https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/${cleanIco}`,
          { method: 'GET', headers: { 'Accept': 'application/json' } }
        );
        let icoStatus = 'neaktivní';
        if (aresRes.ok) {
          const data = await aresRes.json();
          const reg = data.seznamRegistraci || {};
          const vrStav  = (reg.stavZdrojeVr  || '').toUpperCase();
          const rosStav = (reg.stavZdrojeRos || '').toUpperCase();

          const isInactive = !!data.datumZaniku
            || /zanik|vymaz|likvidac|zruš/i.test(vrStav)
            || /zanik|vymaz|likvidac|zruš/i.test(rosStav);

          let isSuspended = false;
          if (!isInactive) {
            try {
              const rzpRes = await fetch(
                `https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty-rzp/${cleanIco}`,
                { method: 'GET', headers: { 'Accept': 'application/json' } }
              );
              if (rzpRes.ok) {
                const rzpData = await rzpRes.json();
                const zaznam = (rzpData.zaznamy || [])[0] || {};
                const zs = zaznam.zivnostiStav || {};
                const aktivni   = zs.pocetAktivnich    ?? null;
                const prerusene = zs.pocetPrerusenych   ?? 0;
                const pozast    = zs.pocetPozastavenych ?? 0;
                const celkem    = zs.pocetCelkem        ?? 0;
                if (celkem > 0 && aktivni === 0 && (prerusene > 0 || pozast > 0)) {
                  isSuspended = true;
                }
              }
            } catch (_) { /* RZP nedostupné – ignoruj */ }
          }

          icoStatus = isInactive ? 'neaktivní' : isSuspended ? 'pozastavené' : 'aktivní';
        }
        await fetch(`${API_URL}/companies/${company._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ icoStatus }),
        });
        setContacts(prev => prev.map(c => c._id === company._id ? { ...c, icoStatus } : c));
        setBatchGroups(prevGroups => {
          const newGroups = { ...prevGroups };
          Object.keys(newGroups).forEach(batch => {
            newGroups[batch] = newGroups[batch].map(c =>
              c._id === company._id ? { ...c, icoStatus } : c
            );
          });
          return newGroups;
        });
        ok++;
      } catch (e) {
        err++;
      }
    }
    setLoading(false);
    window.alert(`✅ Ověřeno ${ok} IČO${err > 0 ? `, ${err} se nepodařilo ověřit.` : '.'}`);
  };

  const deleteSelectedContacts = async () => {
    if (selectedContacts.size === 0) {
      window.alert('Nejdříve označte kontakty, které chcete smazat.');
      return;
    }

    const confirmDelete = window.confirm(
      `⚠️ POZOR! Opravdu chcete NATRVALO SMAZAT ${selectedContacts.size} označených kontaktů?\n\nTato akce je NEVRATNÁ a smaže všechny údaje včetně historie aktivit!\n\nPokračovat?`
    );

    if (!confirmDelete) return;

    // Druhé potvrzení pro bezpečnost
    const doubleConfirm = window.confirm(
      `🔴 POSLEDNÍ VAROVÁNÍ!\n\nChystáte se smazat ${selectedContacts.size} kontaktů NATRVALO.\n\nOpravdu pokračovat?`
    );

    if (!doubleConfirm) return;

    try {
      setLoading(true);
      let deletedCount = 0;
      let errorCount = 0;
      
      for (const contactId of selectedContacts) {
        try {
          const response = await fetch(`${API_URL}/companies/${contactId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          if (response.ok) {
            deletedCount++;
          } else {
            console.error(`Chyba při mazání kontaktu ${contactId}:`, response.statusText);
            errorCount++;
          }
        } catch (error) {
          console.error(`Chyba při mazání kontaktu ${contactId}:`, error);
          errorCount++;
        }
      }
      
      // Zobrazit výsledek
      if (deletedCount > 0 && errorCount === 0) {
        window.alert(`✅ Úspěšně smazáno ${deletedCount} kontaktů!`);
      } else if (deletedCount > 0 && errorCount > 0) {
        window.alert(`⚠️ Smazáno ${deletedCount} kontaktů, ${errorCount} kontaktů se nepodařilo smazat. Zkontrolujte konzoli pro detaily.`);
      } else {
        window.alert(`❌ Nepodařilo se smazat žádný kontakt. Zkontrolujte oprávnění nebo konzoli pro detaily.`);
      }
      
      setSelectedContacts(new Set());
      
      // Obnovit data
      if (selectedUser && selectedUsers.length === 0) {
        loadUserCompaniesAuto(selectedUser);
      } else if (selectedUsers.length > 0) {
        loadSelectedUsersCompanies();
      }
      
      // Aktualizovat hlavní seznam firem v App.js
      if (fetchCompanies) {
        fetchCompanies();
      }
    } catch (error) {
      console.error('Chyba při mazání kontaktů:', error);
      window.alert('Chyba při mazání kontaktů: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ============ DOČASNÉ FUNKCE PRO ČIŠTĚNÍ KONTAKTŮ ============
  
  // Funkce pro čištění kontaktu - odstraní vše od mezery před pomlčkou včetně
  const cleanContactField = (contact) => {
    if (!contact) return contact;
    // Hledá mezeru následovanou pomlčkou a odstraní vše od té mezery včetně
    return contact.replace(/\s+-.*$/, '').trim();
  };

  // Funkce pro generování náhledu čištění
  const generateCleanPreview = () => {
    if (selectedContacts.size === 0) {
      window.alert('Nejdříve označte kontakty, které chcete vyčistit.');
      return;
    }

    const preview = [];
    console.log('🔍 DEBUG: Začínám generovat náhled čištění pro', selectedContacts.size, 'kontaktů');
    
    for (const contactId of selectedContacts) {
      const contact = contacts.find(c => c._id === contactId);
      console.log('\n📋 DEBUG: Kontakt', contact?.name, '- dostupná pole:', Object.keys(contact || {}));
      
      if (contact) {
        // Projít VŠECHNA pole a najít ta obsahující pattern " -"
        const dashPattern = /\s+-.*$/; // Mezera, pomlčka a vše za ní
        let foundFields = 0;
        
        Object.keys(contact).forEach(fieldName => {
          const fieldValue = contact[fieldName];
          
          // Debug: ukázat hodnoty všech string polí
          if (typeof fieldValue === 'string' && fieldValue.length > 0 && !['_id', '__v', 'createdAt', 'updatedAt'].includes(fieldName)) {
            console.log(`   ${fieldName}: "${fieldValue}"`);
            
            // Kontrola pattern pro čištění
            if (dashPattern.test(fieldValue)) {
              const cleanedValue = fieldValue.replace(dashPattern, '').trim();
              console.log(`🎯 NALEZENO K ČIŠTĚNÍ - pole "${fieldName}": "${fieldValue}" → "${cleanedValue}"`);
              
              preview.push({
                id: contactId,
                name: contact.name,
                fieldName: fieldName,
                original: fieldValue,
                cleaned: cleanedValue
              });
              foundFields++;
            }
          }
        });
        
        if (foundFields === 0) {
          console.log('⚠️ DEBUG: Kontakt nemá žádná pole s textem za pomlčkou');
        } else {
          console.log(`✅ DEBUG: Nalezeno ${foundFields} polí k vyčištění`);
        }
      }
    }

    console.log(`\n📊 DEBUG: Celkem nalezeno ${preview.length} polí k vyčištění`);
    
    if (preview.length === 0) {
      console.log('❌ DEBUG: Žádné kontakty k vyčištění - zobrazuji alert');
      window.alert('Žádný z označených kontaktů neobsahuje text za pomlčkou k vyčištění.');
      return;
    }

    setContactCleanPreview(preview);
    setShowContactCleaner(true);
  };

  // Funkce pro aplikování čištění kontaktů
  const applyContactCleaning = async () => {
    if (contactCleanPreview.length === 0) return;

    try {
      setIsCleaningContacts(true);
      setCleaningProgress({ 
        current: 0, 
        total: contactCleanPreview.length, 
        status: 'Čištění kontaktů...' 
      });

      let processed = 0;
      
      for (const item of contactCleanPreview) {
        try {
          // Najít aktuální kontakt a vytvořit aktualizovanou verzi
          const currentContact = contacts.find(c => c._id === item.id);
          if (!currentContact) {
            console.error(`Kontakt ${item.name} nebyl nalezen`);
            continue;
          }

          // Vytvořit aktualizovaný objekt s vyčištěným polem
          const updatedContact = {
            ...currentContact,
            [item.fieldName]: item.cleaned
          };

          const response = await fetch(`${API_URL}/companies/${item.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': currentUserId
            },
            body: JSON.stringify(updatedContact)
          });

          if (response.ok) {
            processed++;
            console.log(`✅ Vyčištěno pole "${item.fieldName}" u kontaktu ${item.name}`);
          } else {
            console.error(`Chyba při čištění kontaktu ${item.name}:`, response.statusText);
          }
        } catch (error) {
          console.error(`Chyba při čištění kontaktu ${item.name}:`, error);
        }

        // Aktualizuj progress
        setCleaningProgress({
          current: processed,
          total: contactCleanPreview.length,
          status: `Vyčištěno ${processed}/${contactCleanPreview.length} kontaktů...`
        });

        // Krátká pauza aby backend stíhal
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      window.alert(`✅ Úspěšně vyčištěno ${processed}/${contactCleanPreview.length} kontaktů!`);
      
      // Zavřít dialog a obnovit data
      setShowContactCleaner(false);
      setContactCleanPreview([]);
      setSelectedContacts(new Set());
      
      // Obnovit data
      if (selectedUser && selectedUsers.length === 0) {
        loadUserCompaniesAuto(selectedUser);
      } else if (selectedUsers.length > 0) {
        loadSelectedUsersCompanies();
      }

    } catch (error) {
      console.error('Chyba při čištění kontaktů:', error);
      window.alert('Chyba při čištění kontaktů: ' + error.message);
    } finally {
      setIsCleaningContacts(false);
      setCleaningProgress({ current: 0, total: 0, status: '' });
    }
  };

  // ============ OTOČENÍ DATUMŮ ============

  // Helper: otočí den a měsíc v ISO date stringu
  const flipISODate = (isoStr) => {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return null;
    const day = d.getUTCDate();
    const month = d.getUTCMonth() + 1; // 1–12
    const year = d.getUTCFullYear();
    if (day > 12) return null; // nový měsíc nemůže být > 12
    // Ověřit, že starý měsíc je platný den v novém měsíci (starý den se stane novým měsícem)
    const daysInNewMonth = new Date(Date.UTC(year, day, 0)).getUTCDate();
    if (month > daysInNewMonth) return null;
    return new Date(Date.UTC(year, day - 1, month)).toISOString();
  };

  // Helper: otočí den a měsíc v českém datovém řetězci "D.M.YYYY"
  const flipCzechDate = (dateStr) => {
    if (!dateStr) return null;
    const match = String(dateStr).match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!match) return null;
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    if (day > 12) return null; // nový měsíc nemůže být > 12
    const daysInNewMonth = new Date(Date.UTC(year, day, 0)).getUTCDate();
    if (month > daysInNewMonth) return null;
    return `${month}.${day}.${year}`;
  };

  const generateDateFlipPreview = () => {
    if (selectedContacts.size === 0) {
      window.alert('Nejdříve označte kontakty, jejichž datumy chcete otočit.');
      return;
    }

    const preview = [];

    for (const contactId of selectedContacts) {
      const contact = contacts.find(c => c._id === contactId);
      if (!contact) continue;

      const fields = [];

      if (contact.nextActionDate) {
        const flipped = flipISODate(contact.nextActionDate);
        if (flipped) {
          fields.push({
            fieldName: 'nextActionDate',
            label: 'Příští akce',
            originalDisplay: new Date(contact.nextActionDate).toLocaleDateString('cs-CZ'),
            flippedValue: flipped,
            flippedDisplay: new Date(flipped).toLocaleDateString('cs-CZ'),
          });
        }
      }

      if (contact.closedDate) {
        const flipped = flipCzechDate(contact.closedDate);
        if (flipped) {
          fields.push({
            fieldName: 'closedDate',
            label: 'Zobchodováno',
            originalDisplay: contact.closedDate,
            flippedValue: flipped,
            flippedDisplay: flipped,
          });
        }
      }

      if (contact.rsEndDate) {
        const flipped = flipCzechDate(contact.rsEndDate);
        if (flipped) {
          fields.push({
            fieldName: 'rsEndDate',
            label: 'Konec RS',
            originalDisplay: contact.rsEndDate,
            flippedValue: flipped,
            flippedDisplay: flipped,
          });
        }
      }

      if (fields.length > 0) {
        preview.push({ id: contactId, name: contact.name, fields });
      }
    }

    if (preview.length === 0) {
      window.alert('Žádný z označených kontaktů nemá datum, které lze otočit (den musí být ≤ 12 a nový den musí být platný v cílovém měsíci).');
      return;
    }

    setDateFlipPreview(preview);
    setShowDateFlipper(true);
  };

  const applyDateFlip = async () => {
    if (dateFlipPreview.length === 0) return;

    try {
      setIsFlippingDates(true);
      setDateFlipProgress({ current: 0, total: dateFlipPreview.length, status: 'Otáčení datumů...' });

      let processed = 0;

      for (const item of dateFlipPreview) {
        try {
          const updates = {};
          for (const field of item.fields) {
            updates[field.fieldName] = field.flippedValue;
          }

          const response = await fetch(`${API_URL}/companies/${item.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-user-id': currentUserId },
            body: JSON.stringify(updates),
          });

          if (response.ok) processed++;
          else console.error(`Chyba při otáčení datumu firmy ${item.name}:`, response.statusText);
        } catch (error) {
          console.error(`Chyba při otáčení datumu firmy ${item.name}:`, error);
        }

        setDateFlipProgress({ current: processed, total: dateFlipPreview.length, status: `Zpracováno ${processed}/${dateFlipPreview.length} firem...` });
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      window.alert(`✅ Úspěšně otočeny datumy u ${processed}/${dateFlipPreview.length} firem!`);
      setShowDateFlipper(false);
      setDateFlipPreview([]);
      setSelectedContacts(new Set());

      if (selectedUser && selectedUsers.length === 0) {
        loadUserCompaniesAuto(selectedUser);
      } else if (selectedUsers.length > 0) {
        loadSelectedUsersCompanies();
      }
    } catch (error) {
      console.error('Chyba při otáčení datumů:', error);
      window.alert('Chyba při otáčení datumů: ' + error.message);
    } finally {
      setIsFlippingDates(false);
      setDateFlipProgress({ current: 0, total: 0, status: '' });
    }
  };

  // ============ OPRAVA TELEFONŮ ============

  // Helper: naformátuje české 9-místné telefonní číslo na "+420 XXX XXX XXX"
  const formatCzechPhone = (phone) => {
    if (!phone) return null;
    let digits = String(phone).replace(/\D/g, '');
    if (digits.startsWith('420')) digits = digits.slice(3);
    if (digits.length !== 9) return null;
    return `+420 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  };

  const generatePhoneFixPreview = () => {
    if (selectedContacts.size === 0) {
      window.alert('Nejdříve označte kontakty, jejichž telefony chcete opravit.');
      return;
    }

    const preview = [];

    for (const contactId of selectedContacts) {
      const contact = contacts.find(c => c._id === contactId);
      if (!contact || !contact.contacts || contact.contacts.length === 0) continue;

      const phones = [];
      contact.contacts.forEach((c, idx) => {
        if (c.phone) {
          const formatted = formatCzechPhone(c.phone);
          if (formatted && formatted !== c.phone) {
            phones.push({ contactIndex: idx, contactName: c.name || `Kontakt ${idx + 1}`, original: c.phone, formatted });
          }
        }
      });

      if (phones.length > 0) {
        preview.push({ id: contactId, name: contact.name, phones });
      }
    }

    if (preview.length === 0) {
      window.alert('Žádný z označených kontaktů nemá telefon k opravě (čísla jsou již ve správném formátu nebo nejde o platné 9-místné české číslo).');
      return;
    }

    setPhoneFixPreview(preview);
    setShowPhoneFixer(true);
  };

  const applyPhoneFix = async () => {
    if (phoneFixPreview.length === 0) return;

    try {
      setIsFixingPhones(true);
      setPhoneFixProgress({ current: 0, total: phoneFixPreview.length, status: 'Oprava telefonů...' });

      let processed = 0;

      for (const item of phoneFixPreview) {
        try {
          const currentContact = contacts.find(c => c._id === item.id);
          if (!currentContact) continue;

          const updatedContacts = [...(currentContact.contacts || [])];
          for (const phone of item.phones) {
            if (updatedContacts[phone.contactIndex]) {
              updatedContacts[phone.contactIndex] = {
                ...updatedContacts[phone.contactIndex],
                phone: phone.formatted,
              };
            }
          }

          const response = await fetch(`${API_URL}/companies/${item.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-user-id': currentUserId },
            body: JSON.stringify({ contacts: updatedContacts }),
          });

          if (response.ok) processed++;
          else console.error(`Chyba při opravě telefonu firmy ${item.name}:`, response.statusText);
        } catch (error) {
          console.error(`Chyba při opravě telefonu firmy ${item.name}:`, error);
        }

        setPhoneFixProgress({ current: processed, total: phoneFixPreview.length, status: `Opraveno ${processed}/${phoneFixPreview.length} firem...` });
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      window.alert(`✅ Úspěšně opraveny telefony u ${processed}/${phoneFixPreview.length} firem!`);
      setShowPhoneFixer(false);
      setPhoneFixPreview([]);
      setSelectedContacts(new Set());

      if (selectedUser && selectedUsers.length === 0) {
        loadUserCompaniesAuto(selectedUser);
      } else if (selectedUsers.length > 0) {
        loadSelectedUsersCompanies();
      }
    } catch (error) {
      console.error('Chyba při opravě telefonů:', error);
      window.alert('Chyba při opravě telefonů: ' + error.message);
    } finally {
      setIsFixingPhones(false);
      setPhoneFixProgress({ current: 0, total: 0, status: '' });
    }
  };

  // ============ KONEC DOČASNÝCH FUNKCÍ ============

  // ============ AKTUALIZACE ADRES Z ARES ============
  const handleUpdateAddresses = async () => {
    if (selectedContacts.size === 0) {
      window.alert('Nejdříve označte kontakty, jejichž adresy chcete aktualizovat.');
      return;
    }

    const toUpdate = [...selectedContacts]
      .map(id => contacts.find(c => c._id === id))
      .filter(c => c && c.ico && c.ico.trim().length === 8);

    if (toUpdate.length === 0) {
      window.alert('Žádná z označených firem nemá platné 8-místné IČO pro vyhledání v ARES.');
      return;
    }

    if (!window.confirm(`Aktualizovat adresu a kraj u ${toUpdate.length} firem z ARES (rejstřík firem)? Stávající adresa bude přepsána.`)) return;

    setIsUpdatingAddresses(true);
    setAddressUpdateProgress({ current: 0, total: toUpdate.length, status: 'Načítám data z ARES...' });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < toUpdate.length; i++) {
      const company = toUpdate[i];
      setAddressUpdateProgress({ current: i + 1, total: toUpdate.length, status: `Zpracovávám: ${company.name}` });
      try {
        const aresData = await fetchCompanyFromARES(company.ico.trim());
        if (!aresData || !aresData.address) { failed++; continue; }

        // Sestavíme update payload — vždy aktualizujeme adresu, a kraj pokud ho ARES vrátil
        const updatePayload = { address: aresData.address };
        if (aresData.kraj) updatePayload.kraj = aresData.kraj;

        const response = await fetch(`${API_URL}/companies/${company._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'x-user-id': currentUserId },
          body: JSON.stringify(updatePayload),
        });

        if (response.ok) success++;
        else failed++;
      } catch {
        failed++;
      }
      // Pauza aby ARES API nebyl zahlcen
      await new Promise(r => setTimeout(r, 300));
    }

    setIsUpdatingAddresses(false);
    setAddressUpdateProgress({ current: 0, total: 0, status: '' });
    window.alert(`✅ Aktualizováno: ${success} firem\n❌ Nepodařilo se: ${failed} firem`);

    if (selectedUser && selectedUsers.length === 0) {
      loadUserCompaniesAuto(selectedUser);
    } else if (selectedUsers.length > 0) {
      loadSelectedUsersCompanies();
    }
  };

  // ============ KONEC AKTUALIZACE ADRES ============

  // ============ HROMADNÉ OVĚŘENÍ SIGNÁLU ============
  const handleBulkSignal = async () => {
    if (selectedContacts.size === 0) {
      window.alert('Nejdříve označte kontakty, jejichž signál chcete ověřit.');
      return;
    }
    const toCheck = [...selectedContacts]
      .map(id => contacts.find(c => c._id === id))
      .filter(c => c && c.address && c.address.trim().length > 3);

    if (toCheck.length === 0) {
      window.alert('Žádná z označených firem nemá vyplněnou adresu.');
      return;
    }

    if (!window.confirm(`Ověřit signál Vodafone LTE pro ${toCheck.length} firem? Může trvat několik minut.`)) return;

    setIsBulkSignal(true);
    setBulkSignalProgress({ current: 0, total: toCheck.length, status: 'Spouštím...' });

    const dbmToSignal = (gi) => {
      if (!gi || gi === 0) return null;
      const dbm = gi / 100;
      let bars, quality;
      if (dbm >= -65)       { bars = 5; quality = 'Výborný'; }
      else if (dbm >= -75)  { bars = 4; quality = 'Dobrý'; }
      else if (dbm >= -85)  { bars = 3; quality = 'Slabý'; }
      else if (dbm >= -95)  { bars = 2; quality = 'Velmi slabý'; }
      else if (dbm >= -110) { bars = 1; quality = 'Kritický'; }
      else                  { bars = 0; quality = 'Kritický'; }
      return { dbm, bars, quality };
    };

    const fetchWms = async (lat, lon, layer) => {
      const R = 6378137;
      const toRad = d => d * Math.PI / 180;
      const xC = R * toRad(lon);
      const yC = R * Math.log(Math.tan(Math.PI / 4 + toRad(lat) / 2));
      const half = 2500;
      const bbox = `${xC - half},${yC - half},${xC + half},${yC + half}`;
      const q = new URLSearchParams({
        REQUEST: 'GetFeatureInfo', SERVICE: 'WMS', VERSION: '1.1.1',
        info_format: 'application/json', feature_count: '1',
        X: '128', Y: '128', WIDTH: '256', HEIGHT: '256',
        SRS: 'EPSG:3857', BBOX: bbox,
        QUERY_LAYERS: `mc10:${layer}`, LAYERS: `mc10:${layer}`,
      });
      const res = await fetch(`https://vportal.ctu.gov.cz/geo/mc10/wms?${q}`);
      const data = await res.json();
      return data?.features?.[0]?.properties?.GRAY_INDEX ?? null;
    };

    let success = 0, failed = 0;

    for (let i = 0; i < toCheck.length; i++) {
      const company = toCheck[i];
      setBulkSignalProgress({ current: i + 1, total: toCheck.length, status: `Geocoding: ${company.name}` });

      try {
        // 1) Geocoding
        const geoRes = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&countrycodes=cz&addressdetails=1&limit=1&q=${encodeURIComponent(company.address)}`,
          { headers: { 'Accept-Language': 'cs', 'User-Agent': 'CRM-Vodafone/1.0' } }
        );
        const geoData = await geoRes.json();
        if (!geoData.length) { failed++; await new Promise(r => setTimeout(r, 1100)); continue; }

        const lat = parseFloat(geoData[0].lat);
        const lon = parseFloat(geoData[0].lon);

        // 2) WMS – Vodafone LTE + 5G
        setBulkSignalProgress({ current: i + 1, total: toCheck.length, status: `WMS: ${company.name}` });
        const [giLte, gi5g] = await Promise.all([
          fetchWms(lat, lon, 'cp_03_4g_d'),
          fetchWms(lat, lon, 'cp_03_5g_d'),
        ]);

        const lteSignal  = dbmToSignal(giLte);
        const fiveGSignal = dbmToSignal(gi5g);
        const savedAt = new Date().toISOString();

        // signal object pro sloupec tabulky (podle LTE)
        const signal = lteSignal
          ? { bars: lteSignal.bars, quality: lteSignal.quality, dbm: lteSignal.dbm, savedAt }
          : { bars: 0, quality: 'Neznámý', dbm: null, savedAt };

        // coverageSnapshot pro kartu firmy
        const coverageSnapshot = {
          savedAt,
          address: company.address,
          lte:   lteSignal  ? { dbm: lteSignal.dbm,   quality: lteSignal.quality   } : null,
          fiveG: fiveGSignal ? { dbm: fiveGSignal.dbm, quality: fiveGSignal.quality } : null,
          curOp: null, curLte: null, curFiveG: null,
        };

        // 3) Uložit do DB
        await fetch(`${API_URL}/companies/${company._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'x-user-id': currentUserId },
          body: JSON.stringify({ signal, coverageSnapshot }),
        });

        setContacts(prev => prev.map(c => c._id === company._id ? { ...c, signal, coverageSnapshot } : c));
        success++;
      } catch {
        failed++;
      }

      // Nominatim rate limit: max 1 req/s
      await new Promise(r => setTimeout(r, 1200));
    }

    setIsBulkSignal(false);
    setBulkSignalProgress({ current: 0, total: 0, status: '' });
    window.alert(`✅ Ověřeno: ${success} firem\n❌ Nepodařilo se: ${failed} firem`);
  };
  // ============ KONEC HROMADNÉHO OVĚŘENÍ SIGNÁLU ============

  // Funkce pro přesun kontaktů mezi šaržemi s možností vytvoření nové
  const transferContactsBetweenBatches = async () => {
    if (selectedContacts.size === 0) {
      window.alert('Nejdříve označte kontakty, které chcete přesunout.');
      return;
    }

    let finalTargetBatch = targetBatch;
    
    // Pokud je zadán název nové šarže, použij ji
    if (newBatchName.trim()) {
      finalTargetBatch = newBatchName.trim();
    }

    if (!finalTargetBatch) {
      window.alert('Vyberte cílovou šarži nebo zadejte název nové šarže.');
      return;
    }

    const confirmTransfer = window.confirm(
      `Opravdu chcete přesunout ${selectedContacts.size} označených kontaktů do šarže "${finalTargetBatch}"?`
    );

    if (!confirmTransfer) return;

    const movedCount = selectedContacts.size;

    try {
      setLoading(true);
      setLoadingProgress({ current: 0, total: movedCount, status: 'Přesouvám kontakty...', isTransfer: true, eta: null });

      const res = await fetch(`${API_URL}/companies/bulk-transfer-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyIds: [...selectedContacts],
          targetBatchName: finalTargetBatch,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Chyba při hromadném přesunu');
      }

      setSelectedContacts(new Set());
      setShowBatchTransfer(false);
      setTargetBatch('');
      setNewBatchName('');
      setLoadingProgress({ current: 0, total: 0, status: 'Načítám kontakty...' });

      // Obnovit data – await zajistí správný loading stav
      if (selectedUser && selectedUsers.length === 0) {
        await loadUserCompaniesAuto(selectedUser);
      } else if (selectedUsers.length > 0) {
        await loadSelectedUsersCompanies();
      }

      if (fetchCompanies) fetchCompanies();
      window.alert(`Úspěšně přesunuto ${movedCount} kontaktů do šarže "${finalTargetBatch}"!`);
    } catch (error) {
      console.error('Chyba při přesunu kontaktů:', error);
      window.alert('Chyba při přesunu kontaktů: ' + error.message);
    } finally {
      setLoading(false);
      setLoadingProgress({ current: 0, total: 0, status: '' });
    }
  };

  // Funkce pro načtení kontaktů konkrétního uživatele s paginací
  const loadUserCompanies = async (userId, page = 1, loadAll = false) => {
    try {
      setLoading(true);
      setLoadingCancelled(false); // Reset cancel stavu
      setLoadingProgress({ current: 0, total: 0, status: 'Příprava načítání...' });
      
      if (loadAll) {
        // Pro menší uživatele načti všechno najednou
        const apiUrl = `${API_URL}/companies?userId=${userId}&all=true`;
        const userCompaniesRes = await fetch(apiUrl);
        
        if (userCompaniesRes.ok) {
          const response = await userCompaniesRes.json();
          
          // Kontrola formátu odpovědi
          let userCompanies;
          if (response.companies) {
            userCompanies = response.companies;
          } else if (Array.isArray(response)) {
            userCompanies = response;
          } else {
            userCompanies = [];
          }
          
          // Seskupit podle názvu šarže
          const groups = userCompanies.reduce((acc, company) => {
            const batchName = company.batchName || 'Bez šarže';
            if (!acc[batchName]) {
              acc[batchName] = [];
            }
            acc[batchName].push(company);
            return acc;
          }, {});
          
          setBatchGroups(groups);
          setContacts(userCompanies);
        }
      } else {
        // Pro velkého uživatele (databáze) použij postupné načítání všech dat po dávkách
        let allCompanies = [];
        let currentPage = 1;
        let hasMore = true;
        let totalEstimate = 0;
        
        // Nejdříve získej celkový počet kontaktů
        try {
          const countUrl = `${API_URL}/companies/count?userId=${userId}`;
          console.log('🔍 Dotazuji se na počet kontaktů:', countUrl);
          const countRes = await fetch(countUrl);
          if (countRes.ok) {
            const countData = await countRes.json();
            totalEstimate = countData.count || 0;
            console.log('📊 Celkový počet kontaktů:', totalEstimate);
            setLoadingProgress({ current: 0, total: totalEstimate, status: 'Načítání kontaktů...' });
          } else {
            console.warn('⚠️ Count endpoint vrátil chybu:', countRes.status, countRes.statusText);
            setLoadingProgress({ current: 0, total: 0, status: 'Načítání kontaktů...' });
          }
        } catch (e) {
          console.warn('⚠️ Nepodařilo se získat celkový počet kontaktů:', e.message);
          setLoadingProgress({ current: 0, total: 0, status: 'Načítání kontaktů...' });
        }
        
        console.log(`� Načítám všechny kontakty po stránkách (celkem odhadováno: ${totalEstimate})...`);
        
        while (hasMore && !loadingCancelled) {
          const chunkSize = getOptimalChunkSize(currentPage, totalEstimate);
          const apiUrl = `${API_URL}/companies?userId=${userId}&page=${currentPage}&limit=${chunkSize}`;
          const userCompaniesRes = await fetch(apiUrl);
          
          if (userCompaniesRes.ok) {
            const response = await userCompaniesRes.json();
            
            let pageCompanies = [];
            if (response.companies) {
              pageCompanies = response.companies;
              // Robustnější detekce konce - použij backend info, ale i kontroluj počet
              if (response.pagination?.hasNextPage !== undefined) {
                hasMore = response.pagination.hasNextPage;
              } else {
                // Fallback - pokud dostaneme méně než aktuální chunk size, je to konec
                hasMore = pageCompanies.length === chunkSize;
              }
              console.log(`📄 Stránka ${currentPage}: načteno ${pageCompanies.length} kontaktů (chunk: ${chunkSize}), hasNextPage: ${hasMore}`);
            } else if (Array.isArray(response)) {
              pageCompanies = response;
              // Pro pole - pokud dostaneme méně než aktuální chunk size, je to konec
              hasMore = pageCompanies.length === chunkSize;
            } else {
              hasMore = false;
            }
            
            // DŮLEŽITÉ: Nejdříve kontrola prázdné stránky před přidáním do allCompanies
            if (pageCompanies.length === 0) {
              console.log('⏹️ Žádné další kontakty k načtení, končíme...');
              hasMore = false;
              break; // Ukončit smyčku ihned
            }
            
            allCompanies = [...allCompanies, ...pageCompanies];
            
            // Aktualizuj progress
            const progressStatus = totalEstimate > 0 
              ? `Načítání ${allCompanies.length}/${totalEstimate} kontaktů...`
              : `Načteno ${allCompanies.length} kontaktů...`;
            setLoadingProgress({ 
              current: allCompanies.length, 
              total: totalEstimate, 
              status: progressStatus 
            });
            
            console.log(`✅ Stránka ${currentPage}: ${pageCompanies.length} firem (celkem: ${allCompanies.length})`);
            console.log(`🔄 Pokračovat na další stránku? hasMore = ${hasMore}`);
            
            currentPage++;
            
            // Bezpečnostní limit - max 5000 stránek (podporuje až 5 milionů firem)
            if (currentPage > 5000) {
              console.warn('⚠️ Dosažen bezpečnostní limit 5000 stránek');
              hasMore = false;
            }
          } else {
            console.error(`❌ Chyba při načítání stránky ${currentPage}:`, userCompaniesRes.statusText);
            hasMore = false;
          }
        }
        
        console.log(`🎉 Načteno celkem ${allCompanies.length} firem`);
        
        // Seskupit podle názvu šarže
        const groups = allCompanies.reduce((acc, company) => {
          const batchName = company.batchName || 'Bez šarže';
          if (!acc[batchName]) {
            acc[batchName] = [];
          }
          acc[batchName].push(company);
          return acc;
        }, {});
        
        setBatchGroups(groups);
        setContacts(allCompanies);
      }
      
      // Automaticky skrýt sloupec "Vlastník" při zobrazení jednotlivého uživatele
      if (visibleColumns.owner) {
        const updatedVisibleColumns = { ...visibleColumns, owner: false };
        setVisibleColumns(updatedVisibleColumns);
        saveCMPref('columns', updatedVisibleColumns);
      }
      
      // Aktualizovat seznam dostupných šarží
      const allGroups = loadAll ? batchGroups : (Object.keys(batchGroups).length > 0 ? batchGroups : {});
      const batches = Object.keys(allGroups).filter(name => name !== 'Bez šarže');
      setAvailableBatches(batches);
    } catch (error) {
      console.error('Chyba při načítání firem uživatele:', error);
      setBatchGroups({});
      setContacts([]);
      setAvailableBatches([]);
    } finally {
      setLoading(false);
      setLoadingProgress({ current: 0, total: 0, status: '' });
      if (loadingCancelled) {
        console.log('📋 Načítání bylo zrušeno uživatelem');
        setContacts([]);
        setBatchGroups({});
        setAvailableBatches([]);
      }
    }
  };

  // Funkce pro načtení kontaktů všech uživatelů s podporou paginace
  const loadAllUsersCompanies = async () => {
    try {
      setLoading(true);
      setLoadingCancelled(false); // Reset cancel stavu
      setLoadingProgress({ current: 0, total: users.length, status: 'Příprava načítání všech uživatelů...' });
      
      // Načíst kontakty všech uživatelů paralelně (po batchích pro menší zátěž serveru)
      const allCompanies = [];
      const allGroups = {};
      let processedUsers = 0;
      
      const PARALLEL_USERS = 3; // Načítej max 3 uživatele současně
      
      // Rozdělí uživatele do batchů
      const userBatches = [];
      for (let i = 0; i < users.length; i += PARALLEL_USERS) {
        userBatches.push(users.slice(i, i + PARALLEL_USERS));
      }
      
      for (const userBatch of userBatches) {
        // Načti všechny uživatele v batchi paralelně
        const batchPromises = userBatch.map(async (user) => {
        try {
          setLoadingProgress({ 
            current: processedUsers, 
            total: users.length, 
            status: `Načítám kontakty uživatele ${user.username}...` 
          });
          
          // Progresivní načítání pro každého uživatele po stránkách
          let userAllCompanies = [];
          let currentPage = 1;
          let hasMore = true;
          
          while (hasMore && !loadingCancelled) {
            const chunkSize = getOptimalChunkSize(currentPage);
            const apiUrl = `${API_URL}/companies?userId=${user._id}&page=${currentPage}&limit=${chunkSize}`;
            const userCompaniesRes = await fetch(apiUrl);
            
            if (userCompaniesRes.ok) {
              const response = await userCompaniesRes.json();
              
              let pageCompanies = [];
              if (response.companies) {
                pageCompanies = response.companies;
                // Robustnější detekce konce - použij backend info, ale i kontroluj počet
                if (response.pagination?.hasNextPage !== undefined) {
                  hasMore = response.pagination.hasNextPage;
                } else {
                  // Fallback - pokud dostaneme méně než aktuální chunk size, je to konec
                  hasMore = pageCompanies.length === chunkSize;
                }
              } else if (Array.isArray(response)) {
                pageCompanies = response;
                // Pro pole - pokud dostaneme méně než aktuální chunk size, je to konec
                hasMore = pageCompanies.length === chunkSize;
              } else {
                hasMore = false;
              }
              
              // DŮLEŽITÉ: Nejdříve kontrola prázdné stránky před přidáním do userAllCompanies
              if (pageCompanies.length === 0) {
                console.log(`⏹️ Žádné další kontakty k načtení pro uživatele ${user.username}, končíme...`);
                hasMore = false;
                break; // Ukončit smyčku ihned
              }
              
              userAllCompanies = [...userAllCompanies, ...pageCompanies];
              
              // Aktualizuj progress pro aktuálního uživatele
              setLoadingProgress({ 
                current: processedUsers, 
                total: users.length, 
                status: `Načítám kontakty uživatele ${user.username} (${userAllCompanies.length} kontaktů)...` 
              });
              
              console.log(`✅ ${user.username} - Stránka ${currentPage}: ${pageCompanies.length} firem (chunk: ${chunkSize}, celkem: ${userAllCompanies.length})`);
              console.log(`🔄 Pokračovat na další stránku pro ${user.username}? hasMore = ${hasMore}`);
              
              currentPage++;
              
              // Bezpečnostní limit - max 5000 stránek
              if (currentPage > 5000) {
                console.warn('⚠️ Dosažen bezpečnostní limit 5000 stránek pro uživatele', user.username);
                hasMore = false;
              }
            } else {
              hasMore = false;
            }
          }
          
          // Přidat informaci o vlastníkovi do každého kontaktu
          const companiesWithOwner = userAllCompanies.map(company => ({
            ...company,
            ownerInfo: {
              username: user.username,
              email: user.email,
              id: user._id
            }
          }));
          
          return {
            user,
            companies: companiesWithOwner
          };
          
        } catch (error) {
          console.error(`Chyba při načítání kontaktů uživatele ${user.username}:`, error);
          return {
            user,
            companies: []
          };
        }
      });
      
      // Počkej na dokončení všech uživatelů v batchi
      const batchResults = await Promise.all(batchPromises);
      
      // Zpracuj výsledky batche
      batchResults.forEach(result => {
        const { user, companies } = result;
        
        allCompanies.push(...companies);
        
        // Seskupit podle šarží s prefixem uživatele
        companies.forEach(company => {
          const batchName = company.batchName || 'Bez šarže';
          const groupKey = `${user.username} - ${batchName}`;
          
          if (!allGroups[groupKey]) {
            allGroups[groupKey] = [];
          }
          allGroups[groupKey].push(company);
        });
        
        processedUsers++;
        
        // Aktualizuj progress
        setLoadingProgress({ 
          current: processedUsers, 
          total: users.length, 
          status: `Dokončeno načítání uživatele ${user.username} (${companies.length} kontaktů)...` 
        });
      });
      }
      
      setBatchGroups(allGroups);
      setContacts(allCompanies);
      
      // Automaticky zapnout sloupec "Vlastník" při zobrazení všech uživatelů
      const updatedVisibleColumns = { ...visibleColumns, owner: true };
      setVisibleColumns(updatedVisibleColumns);
      saveCMPref('columns', updatedVisibleColumns);
      
      // Aktualizovat seznam dostupných šarží
      const batches = Object.keys(allGroups).filter(name => !name.includes('Bez šarže'));
      setAvailableBatches(batches);
      
    } catch (error) {
      console.error('Chyba při načítání kontaktů všech uživatelů:', error);
      setBatchGroups({});
      setContacts([]);
      setAvailableBatches([]);
    } finally {
      setLoading(false);
      if (loadingCancelled) {
        console.log('📋 Načítání všech uživatelů bylo zrušeno uživatelem');
        setContacts([]);
        setBatchGroups({});
        setAvailableBatches([]);
      }
    }
  };

  // Nový jednoduchý globální vyhledavač
  const searchGlobally = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      setGlobalSearchResults([]);
      return;
    }

    try {
      setIsGlobalSearching(true);
      console.log('🔍 Globální vyhledávání:', searchTerm.trim());
      
      // Vyhledávání napříč celou databází bez ohledu na uživatele
      const searchUrl = `${API_URL}/companies?search=${encodeURIComponent(searchTerm.trim())}&limit=20`;
      
      const response = await fetch(searchUrl);
      
      if (response.ok) {
        let data = await response.json();
        let companies = data.companies || data || [];
        
        if (!Array.isArray(companies)) {
          companies = [];
        }

        console.log('📊 Nalezeno firem:', companies.length);

        // Přidej informace o vlastníkovi ke každé společnosti
        const companiesWithOwners = companies.map(company => {
          console.log('🔍 Firma:', company.name, 'createdBy typ:', typeof company.createdBy, 'hodnota:', company.createdBy);
          
          let ownerInfo;
          
          // Zkontroluj jestli je createdBy už populovaný objekt
          if (company.createdBy && typeof company.createdBy === 'object' && company.createdBy.name) {
            console.log('� Populovaný vlastník:', company.createdBy.name);
            ownerInfo = {
              username: company.createdBy.name,
              email: company.createdBy.email || '',
              id: company.createdBy._id
            };
          } else if (company.createdBy) {
            // Fallback - hledej podle ID v users array
            const owner = users.find(user => user._id === company.createdBy);
            console.log('👤 Nalezený vlastník podle ID:', owner ? owner.username : 'NENALEZEN');
            ownerInfo = owner ? {
              username: owner.username,
              email: owner.email,
              id: owner._id
            } : null;
          } else {
            console.log('❌ createdBy je prázdné');
            ownerInfo = null;
          }
          
          return {
            ...company,
            ownerInfo: ownerInfo || { 
              username: 'Neznámý uživatel', 
              email: '', 
              id: null
            }
          };
        });

        setGlobalSearchResults(companiesWithOwners);
        console.log('🎯 Nalezeno:', companiesWithOwners.length, 'výsledků');
        
      } else {
        console.error('Chyba při vyhledávání:', response.status, response.statusText);
        setGlobalSearchResults([]);
      }
    } catch (error) {
      console.error('Chyba při globálním vyhledávání:', error);
      setGlobalSearchResults([]);
    } finally {
      setIsGlobalSearching(false);
    }
  };

  // Debounced vyhledávání
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchTerm = globalSearch.trim();
      if (searchTerm.length >= 3) {
        searchGlobally(searchTerm);
      } else {
        setGlobalSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [globalSearch, users]);

  // Handler pro prokliknutí na výsledek vyhledávání
  const handleSearchResultClick = (company) => {
    if (company && company._id) {
      // Vymaž vyhledávání
      setGlobalSearch('');
      setGlobalSearchResults([]);
      
      // Naviguj na detail firmy
      console.log('🎯 Otevírám firmu:', company.name, 'ID:', company._id);
      navigate(`/companies/${company._id}`);
    }
  };

  const handleUserChange = (userId) => {
    setSelectedUser(userId);
    
    // Reset multi-select při výběru jiné možnosti
    if (userId !== 'MULTI_SELECT') {
      setSelectedUsers([]);
    }
    
    if (userId === 'ALL_USERS') {
      loadAllUsersCompanies();
    } else if (userId === 'MULTI_SELECT') {
      setShowUserSelector(true);
    } else if (userId) {
      loadUserCompaniesAuto(userId, 1);
    } else {
      setBatchGroups({});
      setContacts([]);
      setAvailableBatches([]);
    }
  };

  // Funkce pro multi-select uživatelů
  const handleUserSelection = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  // Funkce pro výběr všech uživatelů
  const selectAllUsers = () => {
    setSelectedUsers(users.map(user => user._id));
  };

  // Funkce pro zrušení výběru všech uživatelů
  const deselectAllUsers = () => {
    setSelectedUsers([]);
  };

  // Funkce pro potvrzení multi-select
  const confirmUserSelection = () => {
    // Reset single user selection při multi-select
    setSelectedUser('');
    
    if (selectedUsers.length === 0) {
      setBatchGroups({});
      setContacts([]);
      setAvailableBatches([]);
    } else {
      loadSelectedUsersCompanies();
    }
    setShowUserSelector(false);
  };

  // Funkce pro načtení kontaktů vybraných uživatelů s postupným načítáním
  const loadSelectedUsersCompanies = async () => {
    try {
      setLoading(true);
      setLoadingProgress({ current: 0, total: selectedUsers.length, status: 'Začínám načítat kontakty...' });
      
      const allCompanies = [];
      const allGroups = {};
      let currentUserIndex = 0;
      
      for (const userId of selectedUsers) {
        try {
          currentUserIndex++;
          const user = users.find(u => u._id === userId);
          if (!user) continue;
          
          setLoadingProgress({ 
            current: currentUserIndex, 
            total: selectedUsers.length, 
            status: `Načítám kontakty pro ${user.username}...` 
          });
          
          // Kontrola, zda je to databázový uživatel
          const isDatabaseUser = user.role === 'database' || 
                                user.name?.toLowerCase().includes('databáze') ||
                                user.username?.toLowerCase().includes('database');
          
          let userCompanies = [];
          
          if (isDatabaseUser) {
            // Pro databázové uživatele používáme postupné načítání po stránkách
            let currentPage = 1;
            let hasMore = true;
            
            while (hasMore) {
              const apiUrl = `${API_URL}/companies?userId=${userId}&page=${currentPage}&limit=500`;
              const userCompaniesRes = await fetch(apiUrl);
              
              if (userCompaniesRes.ok) {
                const response = await userCompaniesRes.json();
                let pageCompanies;
                
                if (response.companies) {
                  pageCompanies = response.companies;
                  hasMore = response.pagination && response.pagination.hasMore;
                } else if (Array.isArray(response)) {
                  pageCompanies = response;
                  hasMore = response.length === 500; // Pokud je délka rovna limitu, pravděpodobně jsou další stránky
                } else {
                  pageCompanies = [];
                  hasMore = false;
                }
                
                userCompanies.push(...pageCompanies);
                currentPage++;
                
                // Aktualizovat progress
                setLoadingProgress({ 
                  current: currentUserIndex, 
                  total: selectedUsers.length, 
                  status: `Načítám kontakty pro ${user.username}... (${userCompanies.length} kontaktů)` 
                });
                
                // Bezpečnostní limit - max 5000 stránek
                if (currentPage > 5000) {
                  console.warn('⚠️ Dosažen bezpečnostní limit 5000 stránek pro uživatele', user.username);
                  hasMore = false;
                }
              } else {
                console.error(`❌ Chyba při načítání stránky ${currentPage} pro uživatele ${user.username}:`, userCompaniesRes.statusText);
                hasMore = false;
              }
              
              // Malá pauza aby neblokoval UI
              await new Promise(resolve => setTimeout(resolve, 10));
            }
          } else {
            // Pro běžné uživatele také používáme postupné načítání po stránkách
            let currentPage = 1;
            let hasMore = true;
            
            while (hasMore) {
              const apiUrl = `${API_URL}/companies?userId=${userId}&page=${currentPage}&limit=${contactsPerPage}`;
              const userCompaniesRes = await fetch(apiUrl);
              
              if (userCompaniesRes.ok) {
                const response = await userCompaniesRes.json();
                let pageCompanies;
                
                if (response.companies) {
                  pageCompanies = response.companies;
                  hasMore = response.pagination?.hasNextPage || false;
                } else if (Array.isArray(response)) {
                  pageCompanies = response;
                  hasMore = pageCompanies.length === contactsPerPage;
                } else {
                  pageCompanies = [];
                  hasMore = false;
                }
                
                userCompanies.push(...pageCompanies);
                
                // Aktualizovat progress
                setLoadingProgress({ 
                  current: currentUserIndex, 
                  total: selectedUsers.length, 
                  status: `Načítám kontakty pro ${user.username}... (${userCompanies.length} kontaktů)` 
                });
                
                // Pokud nemáme žádné kontakty na této stránce, zastavíme
                if (pageCompanies.length === 0) {
                  hasMore = false;
                }
                
                currentPage++;
                
                // Bezpečnostní limit - max 5000 stránek
                if (currentPage > 5000) {
                  console.warn('⚠️ Dosažen bezpečnostní limit 5000 stránek pro uživatele', user.username);
                  hasMore = false;
                }
              } else {
                console.error(`❌ Chyba při načítání stránky ${currentPage} pro uživatele ${user.username}:`, userCompaniesRes.statusText);
                hasMore = false;
              }
              
              // Malá pauza aby neblokoval UI
              await new Promise(resolve => setTimeout(resolve, 10));
            }
          }
          
          // Přidat informaci o vlastníkovi do každého kontaktu
          const companiesWithOwner = userCompanies.map(company => ({
            ...company,
            ownerInfo: {
              username: user.username,
              email: user.email,
              id: user._id
            }
          }));
          
          allCompanies.push(...companiesWithOwner);
          
          // Seskupit podle šarží s prefixem uživatele
          companiesWithOwner.forEach(company => {
            const batchName = company.batchName || 'Bez šarže';
            const groupKey = `${user.username} - ${batchName}`;
            
            if (!allGroups[groupKey]) {
              allGroups[groupKey] = [];
            }
            allGroups[groupKey].push(company);
          });
          
        } catch (error) {
          console.error(`Chyba při načítání kontaktů pro uživatele ${userId}:`, error);
          // Pokračovat s dalšími uživateli
        }
      }
      
      setBatchGroups(allGroups);
      setContacts(allCompanies);
      
      // Automaticky zapnout sloupec "Vlastník" při zobrazení více uživatelů
      const updatedVisibleColumns = { ...visibleColumns, owner: true };
      setVisibleColumns(updatedVisibleColumns);
      saveCMPref('columns', updatedVisibleColumns);
      
      // Aktualizovat seznam dostupných šarží
      const batches = Object.keys(allGroups).filter(name => !name.includes('Bez šarže'));
      setAvailableBatches(batches);
      
    } catch (error) {
      console.error('Chyba při načítání kontaktů vybraných uživatelů:', error);
      setBatchGroups({});
      setContacts([]);
      setAvailableBatches([]);
    } finally {
      setLoading(false);
      setLoadingProgress({ current: 0, total: 0, status: '' });
    }
  };

  if (loading) {
    const { current, total, status, isTransfer, eta } = loadingProgress;
    const hasProgress = total > 0;
    const progressPercent = hasProgress ? Math.round((current / total) * 100) : 0;

    const formatEta = (secs) => {
      if (!secs || secs <= 0) return null;
      if (secs < 60) return `~${secs} s`;
      const mins = Math.floor(secs / 60);
      const remSecs = secs % 60;
      return remSecs > 0 ? `~${mins} min ${remSecs} s` : `~${mins} min`;
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center shadow-2xl min-w-80">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-2">
            {status || "Načítám kontakty..."}
          </p>
          
          {hasProgress && (
            <>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2">
                <div 
                  className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {progressPercent}% ({current.toLocaleString()} z {total.toLocaleString()})
              </p>
              {isTransfer && eta != null && (
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  ⏱ Odhadovaný čas dokončení: {formatEta(eta)}
                </p>
              )}
            </>
          )}
          
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-2 text-center">
            {isTransfer
              ? "Přesouvám kontakty jeden po druhém..."
              : hasProgress
              ? "Načítám data po dávkách..."
              : "Zpracovávám databázi kontaktů"}
          </p>
          
          {/* Cancel button - skryto při přesouvání kontaktů */}
          {!isTransfer && (
            <button
              onClick={cancelLoading}
              className="mt-4 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-medium transition-colors"
            >
              🚫 Zrušit načítání
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${isDarkMode ? 'from-gray-900 to-gray-800' : 'from-gray-100 to-blue-100'}`}>
      <div className="container mx-auto px-4 py-8">
        
        {/* Hlavička */}
        <PageHeader
          title="Správa kontaktů"
          actions={
            <>
              <button
                onClick={() => setShowAutoVerification(true)}
                className="px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-md transition"
                title="Hromadné ověření IČO, adres a signálu na pozadí bez načítání firem"
              >
                🔄 Automatické ověření
              </button>
              <button
                onClick={() => {
                  setImportUpdateMode(false);
                  setShowImportWizard(true);
                }}
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold shadow-md transition"
                title="Importovat nové kontakty"
              >
                📥 Spustit import
              </button>
              <button
                onClick={() => setShowOperatorVerification(true)}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-md transition"
                title="Ověření operátorů u kontaktů"
              >
                📞 Ověření operátorů
              </button>
              <button
                onClick={() => setShowSimpleCAImport(true)}
                className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white font-semibold transition"
                title="Jednoduchý import CA a PRM Vodafone z Excel souboru"
              >
                📊 Ověření PRM/CA
              </button>
              <button
                onClick={() => setShowMsisdnIcoFix(true)}
                className="px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition"
                title="Nahrát XLSX s telefonem (A) a správným IČO (B), zobrazit změny a potvrdit přepis"
              >
                🛠️ Oprava IČ dle MSISDN
              </button>
              <button
                onClick={() => navigate('/duplicate-checker')}
                className="px-4 py-2 rounded bg-orange-600 hover:bg-orange-700 text-white font-semibold transition"
                title="Najít duplicitní IČO v databázi"
              >
                🔍 Ověření duplicit
              </button>
              <button
                onClick={() => setShowIcoOwnerChecker(true)}
                className="px-4 py-2 rounded bg-purple-600 hover:bg-purple-700 text-white font-semibold transition"
                title="Zjistit vlastníky firem podle seznamu IČO"
              >
                🔎 Kontrola vlastníků IČO
              </button>
              <button
                onClick={() => setShowVraceniDtb(true)}
                className="px-4 py-2 rounded bg-cyan-600 hover:bg-cyan-700 text-white font-semibold transition"
                title="Vložit seznam IČO a získat vlastníky + stavy, export do Excelu"
              >
                📋 Vrácení DTB
              </button>
              <button
                onClick={() => navigate('/bulk-assignment')}
                className="px-4 py-2 rounded bg-teal-600 hover:bg-teal-700 text-white font-semibold transition"
                title="Hromadné přidělování kontaktů podle IČO"
              >
                🔄 Přidělování
              </button>
              <button
                onClick={() => setShowBulkContactImport(true)}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold transition"
                title="Hromadné doplnění kontaktních osob ke společnostem podle IČO"
              >
                👤 Hromadné kontakty
              </button>
              {userRole === 'admin' && (
              <button
                onClick={() => setShowRatings(true)}
                className="px-4 py-2 rounded bg-yellow-500 hover:bg-yellow-600 text-white font-semibold transition"
                title="Hodnocení šarží kontaktů"
              >
                ⭐ Hodnocení
              </button>
              )}
            </>
          }
        />
        
        {/* Nový jednoduchý globální vyhledávač */}
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <h2 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🔍 Globální vyhledávání
          </h2>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Zadejte IČO nebo název firmy. Kliknutím na výsledek se otevře detail firmy.
          </p>
          
          <div className="flex gap-3 items-center">
            <div className="flex-1">
              <input
                type="text"
                placeholder="IČO nebo název firmy..."
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setGlobalSearch('');
                    setGlobalSearchResults([]);
                  }
                }}
                className={`w-full px-4 py-2 border rounded-lg ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
            
            {isGlobalSearching && (
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent mr-2"></div>
                Vyhledávám...
              </div>
            )}
          </div>
          
          {/* Výsledky vyhledávání */}
          {globalSearchResults.length > 0 && (
            <div className={`mt-4 border rounded-lg ${
              isDarkMode ? 'border-gray-600' : 'border-gray-200'
            }`}>
              <div className={`px-4 py-2 text-sm font-medium border-b ${
                isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-gray-50 text-gray-700 border-gray-200'
              }`}>
                Nalezeno {globalSearchResults.length} výsledků
              </div>
              
              <div className="max-h-64 overflow-y-auto">
                {globalSearchResults.map((company) => (
                  <div
                    key={company._id}
                    onClick={() => handleSearchResultClick(company)}
                    className={`px-4 py-3 border-b cursor-pointer transition-colors ${
                      isDarkMode 
                        ? 'border-gray-600 hover:bg-gray-700 text-white' 
                        : 'border-gray-200 hover:bg-gray-50 text-gray-900'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium mb-1">{company.name}</div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          IČO: {company.ico || 'N/A'}
                          {company.address && (
                            <span className="ml-3">📍 {company.address}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className={`text-sm font-medium ${
                          isDarkMode ? 'text-blue-300' : 'text-blue-600'
                        }`}>
                          👤 {company.ownerInfo?.username || 'Neznámý'}
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Klikněte pro otevření
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Zpráva o žádných výsledcích */}
          {globalSearch.length >= 3 && globalSearchResults.length === 0 && !isGlobalSearching && (
            <div className={`mt-4 text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Žádné výsledky pro "{globalSearch}"
            </div>
          )}
        </div>
        

        {/* Výběr uživatele pro správu kontaktů */}
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-64">
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">
                Vyberte uživatele pro správu kontaktů:
              </label>
              <SearchableUserDropdown
                value={selectedUser}
                onChange={handleUserChange}
                users={users}
                specialOptions={[
                  { value: '', label: '-- Vyberte uživatele --' },
                  { value: 'ALL_USERS', label: '👥 Všichni uživatelé' },
                  { value: 'MULTI_SELECT', label: '🎯 Vybrat konkrétní uživatele' },
                ]}
                isDarkMode={isDarkMode}
                className="w-full"
              />
              
              {/* Zobrazit info o vybraných uživatelích při multi-select */}
              {selectedUsers.length > 0 && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Vybraní uživatelé ({selectedUsers.length}): {
                    selectedUsers.map(userId => {
                      const user = users.find(u => u._id === userId);
                      return user ? user.username : 'Neznámý';
                    }).join(', ')
                  }
                </div>
              )}
            </div>
            
            {(selectedUser || selectedUsers.length > 0) && (
              <div className="min-w-44">
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">
                  Položek na stránku:
                </label>
                <select
                  value={contactsPerPage}
                  onChange={(e) => setContactsPerPage(e.target.value === 'all' ? Infinity : parseInt(e.target.value))}
                  className={`w-full px-3 py-2 border rounded-md h-10 ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                  <option value={2500}>2500</option>
                  <option value={5000}>5000</option>
                  <option value={10000}>10000</option>
                </select>
              </div>
            )}
            
            {(selectedUser || selectedUsers.length > 0) && (
              <>
                <button
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                  className={`px-4 py-2 rounded h-10 ${
                    isDarkMode 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {showColumnSettings ? 'Skrýt' : 'Zobrazit'} nastavení sloupců
                </button>
                
                <button
                  onClick={() => setShowBulkExport(true)}
                  className={`px-4 py-2 rounded h-10 ${
                    isDarkMode 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  📊 Hromadný export
                </button>
                
                <button
                  onClick={saveCurrentFilters}
                  className={`px-4 py-2 rounded h-10 ${
                    isDarkMode 
                      ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                      : 'bg-teal-500 hover:bg-teal-600 text-white'
                  }`}
                  title="Uložit aktuální filtry"
                >
                  💾 Uložit filtry
                </button>
                
                <button
                  onClick={() => setShowSavedFilters(!showSavedFilters)}
                  className={`px-4 py-2 rounded h-10 ${
                    isDarkMode 
                      ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                      : 'bg-purple-500 hover:bg-purple-600 text-white'
                  }`}
                >
                  📋 Uložené filtry
                </button>
              </>
            )}
          </div>
          
          {/* Sekce pro práci s označenými kontakty - funguje i pro více vybraných uživatelů */}
          {((selectedUser && selectedUser !== 'ALL_USERS') || selectedUsers.length > 0) && selectedContacts.size > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
              <div className="flex flex-wrap items-center gap-4 mb-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Označeno {selectedContacts.size} kontaktů:
                </span>
                
                <button
                  onClick={() => setSelectedContacts(new Set())}
                  className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-sm"
                >
                  Zrušit označení
                </button>
                
                <button
                  onClick={deleteSelectedContacts}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium"
                  title="Natrvalo smazat označené kontakty"
                >
                  🗑️ Smazat označené ({selectedContacts.size})
                </button>

                <button
                  onClick={verifyIcoStatuses}
                  className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-medium"
                  title="Ověřit stav IČO označených firem přes ARES"
                >
                  🔍 Ověřit IČO ({selectedContacts.size})
                </button>
                
                {/* DOČASNÉ - Tlačítko pro čištění kontaktů */}
                <button
                  onClick={generateCleanPreview}
                  className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm font-medium"
                  title="Vyčistit pole 'kontakt' u označených firem - odstraní vše za pomlčkou"
                >
                  🧹 Vyčistit kontakty ({selectedContacts.size})
                </button>

                {/* DOČASNÉ - Tlačítko pro otočení datumů */}
                <button
                  onClick={generateDateFlipPreview}
                  className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium"
                  title="Otočit den a měsíc v datumech označených firem (např. 5.1.2026 → 1.5.2026)"
                >
                  📅 Otočit datumy ({selectedContacts.size})
                </button>

                {/* DOČASNÉ - Tlačítko pro opravu telefonů */}
                <button
                  onClick={generatePhoneFixPreview}
                  className="px-3 py-1 bg-teal-500 hover:bg-teal-600 text-white rounded text-sm font-medium"
                  title="Opravit formát telefonních čísel u označených firem (např. 777111222 → +420 777 111 222)"
                >
                  📞 Opravit telefony ({selectedContacts.size})
                </button>

                {/* Aktualizace adres z ARES */}
                <button
                  onClick={handleUpdateAddresses}
                  className="px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded text-sm font-medium"
                  title="Aktualizovat adresu a kraj označených firem dle IČO z ARES (rejstřík firem)"
                >
                  🏢 Aktualizovat adresy + kraj ({selectedContacts.size})
                </button>

                {/* Hromadné ověření signálu */}
                <button
                  onClick={handleBulkSignal}
                  disabled={isBulkSignal}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded text-sm font-medium"
                  title="Ověřit signál Vodafone LTE pro označené firmy (dle adresy, data z ČTÚ VPortal)"
                >
                  📡 Ověřit signál ({selectedContacts.size})
                </button>
              </div>
              
              {/* Převod na jiného uživatele */}
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Převést na uživatele:</span>
                <SearchableUserDropdown
                  value={selectedTransferUser}
                  onChange={setSelectedTransferUser}
                  users={users.filter(user => {
                    if (selectedUser && user._id === selectedUser) return false;
                    if (selectedUsers.length > 0 && selectedUsers.includes(user._id)) return false;
                    return true;
                  })}
                  isDarkMode={isDarkMode}
                  compact
                />
                <button
                  onClick={() => transferSelectedContacts(selectedTransferUser)}
                  disabled={!selectedTransferUser}
                  className="px-3 py-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded text-sm"
                >
                  Převést označené
                </button>
              </div>
              
              {/* Přesun do jiné šarže */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Šarže:</span>
                <button
                  onClick={() => setShowBatchTransfer(!showBatchTransfer)}
                  className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded text-sm"
                >
                  {showBatchTransfer ? 'Skrýt' : 'Přesunout do jiné šarže'}
                </button>
              </div>
              
              {/* Formulář pro přesun mezi šaržemi */}
              {showBatchTransfer && (
                <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900 rounded border">
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Existující šarže:</label>
                      <select
                        value={targetBatch}
                        onChange={(e) => {
                          setTargetBatch(e.target.value);
                          setNewBatchName(''); // Reset nové šarže když vyberu existující
                        }}
                        className={`px-3 py-1 border rounded text-sm ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">-- Vyberte existující šarži --</option>
                        {availableBatches.map(batchName => (
                          <option key={batchName} value={batchName}>
                            {batchName}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="text-sm font-medium">Nebo vytvořit novou šarži:</label>
                      <input
                        type="text"
                        value={newBatchName}
                        onChange={(e) => {
                          setNewBatchName(e.target.value);
                          setTargetBatch(''); // Reset existující šarže když zadávám novou
                        }}
                        placeholder="Název nové šarže"
                        className={`px-3 py-1 border rounded text-sm ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={transferContactsBetweenBatches}
                        disabled={!targetBatch && !newBatchName.trim()}
                        className="px-3 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded text-sm"
                      >
                        Přesunout
                      </button>
                      <button
                        onClick={() => {
                          setShowBatchTransfer(false);
                          setTargetBatch('');
                          setNewBatchName('');
                        }}
                        className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-sm"
                      >
                        Zrušit
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nastavení sloupců */}
        {showColumnSettings && (
          <div className={`mb-6 p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              ⚙️ Nastavení sloupců
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Klikněte pro zobrazení/skrytí sloupce. Přetáhněte sloupec pro změnu pořadí.
            </p>
            
            {/* Grid layout pro sloupce */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {columnOrder.map((columnName, index) => {
                const columnLabels = {
                  name: '📋 Název',
                  ico: '🏢 IČO',
                  address: '📍 Adresa',
                  operator: '📱 Operátor',
                  status: '🏷️ Stav',
                  phone: '☎️ Telefon',
                  email: '✉️ Email',
                  contact: '👤 Kontakt',
                  bmsl: '💰 BMSL',
                  forecast: '📊 Forecast',
                  ca: '🆔 CA',
                  executive: '👤 Vlastník firmy CRM',
                  prm: '📞 PRM Vodafone',
                  closedDate: '🎯 Zobchodováno',
                  contractLength: '📅 Délka smlouvy',
                  personType: '🏷️ Typ osoby',
                  nextActionDate: '📅 Datum další akce',
                  owner: '👤 Vlastník',
                  icoStatus: '🟢 Stav IČO',
                  signal: '📡 Signál LTE',
                  kraj: '🗺️ Kraj'
                };

                return (
                  <div
                    key={columnName}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, columnName, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    className={`
                      relative cursor-move transition-all duration-200 p-4 rounded-lg border-2
                      ${visibleColumns[columnName] 
                        ? isDarkMode 
                          ? 'bg-blue-900 border-blue-600 shadow-lg' 
                          : 'bg-blue-50 border-blue-300 shadow-lg'
                        : isDarkMode 
                          ? 'bg-gray-700 border-gray-600' 
                          : 'bg-white border-gray-200'
                      }
                      ${dragOverIndex === index && draggedColumn?.index !== index
                        ? isDarkMode
                          ? 'border-yellow-500 bg-yellow-900'
                          : 'border-yellow-400 bg-yellow-50'
                        : ''
                      }
                      hover:shadow-md transform hover:scale-105
                    `}
                    onClick={() => toggleColumn(columnName)}
                  >
                    {/* Drag handle */}
                    <div className="absolute top-2 right-2 text-gray-400 text-xs">
                      ⋮⋮
                    </div>
                    
                    {/* Pozice */}
                    <div className={`absolute top-2 left-2 text-xs font-bold px-1.5 py-0.5 rounded ${
                      isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                    }`}>
                      #{index + 1}
                    </div>
                    
                    {/* Checkbox a label */}
                    <div className="mt-6 flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={visibleColumns[columnName]}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleColumn(columnName);
                        }}
                        className="mt-1 rounded"
                      />
                      <div className="flex-1">
                        <span className={`block text-sm font-medium leading-tight ${
                          visibleColumns[columnName]
                            ? isDarkMode ? 'text-blue-200' : 'text-blue-800'
                            : isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {columnLabels[columnName]}
                        </span>
                        <span className={`text-xs ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {visibleColumns[columnName] ? 'Zobrazeno' : 'Skryto'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Visual indicator for active state */}
                    {visibleColumns[columnName] && (
                      <div className={`absolute bottom-2 right-2 w-2 h-2 rounded-full ${
                        isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
                      }`}></div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Rychlé akce */}
            <div className="mt-6 flex flex-wrap gap-3 pt-4 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => {
                  const allVisible = Object.fromEntries(
                    Object.keys(visibleColumns).map(key => [key, true])
                  );
                  setVisibleColumns(allVisible);
                  saveCMPref('columns', allVisible);
                }}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  isDarkMode
                    ? 'bg-green-700 hover:bg-green-600 text-white'
                    : 'bg-green-100 hover:bg-green-200 text-green-800'
                }`}
              >
                ✅ Zobrazit vše
              </button>
              
              <button
                onClick={() => {
                  const noneVisible = Object.fromEntries(
                    Object.keys(visibleColumns).map(key => [key, false])
                  );
                  setVisibleColumns(noneVisible);
                  saveCMPref('columns', noneVisible);
                }}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  isDarkMode
                    ? 'bg-red-700 hover:bg-red-600 text-white'
                    : 'bg-red-100 hover:bg-red-200 text-red-800'
                }`}
              >
                ❌ Skrýt vše
              </button>
              
              <button
                onClick={() => {
                  const defaultOrder = ['name', 'ico', 'address', 'kraj', 'nextActionDate', 'operator', 'status', 'contact', 'phone', 'email', 'executive', 'owner', 'bmsl', 'forecast', 'ca', 'prm', 'closedDate', 'contractLength', 'personType'];
                  setColumnOrder(defaultOrder);
                  saveCMPref('columnOrder', defaultOrder);
                }}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  isDarkMode
                    ? 'bg-blue-700 hover:bg-blue-600 text-white'
                    : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                }`}
              >
                🔄 Obnovit pořadí
              </button>
            </div>
          </div>
        )}

        {/* Uložené filtry */}
        {showSavedFilters && (
          <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>🔖 Uložené filtry</h3>
            {savedFilters.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">Žádné uložené filtry</p>
            ) : (
              <div className="space-y-3">
                {savedFilters.map((filterSet) => (
                  <div key={filterSet.id} className={`p-3 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{filterSet.name}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Vytvořeno: {new Date(filterSet.createdAt).toLocaleString('cs-CZ')}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {Object.entries(filterSet.filters)
                            .filter(([, value]) => value.trim() !== '')
                            .map(([column, value]) => (
                              <span key={column} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                                {column}: {value}
                              </span>
                            ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => loadFilterSet(filterSet)}
                          className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm"
                          title="Načíst tento filtr"
                        >
                          📥 Načíst
                        </button>
                        <button
                          onClick={() => deleteSavedFilter(filterSet.id)}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                          title="Smazat tento filtr"
                        >
                          🗑️ Smazat
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Zobrazení šarží kontaktů */}
        {Object.keys(batchGroups).length > 0 ? (
          <div className="space-y-6">
            {/* Informace o aktivních filtrech */}
            {Object.values(columnFilters).some(filter => filter.trim() !== '') && (
              <div className={`p-3 rounded-lg border-l-4 border-blue-500 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-blue-800 dark:text-blue-200">Aktivní filtry</h4>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {Object.entries(columnFilters)
                        .filter(([, value]) => value.trim() !== '')
                        .map(([column, value]) => (
                          <span key={column} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                            {column === 'name' && 'Název'}
                            {column === 'ico' && 'IČO'}
                            {column === 'address' && 'Adresa'}
                            {column === 'kraj' && 'Kraj'}
                            {column === 'operator' && 'Operátor'}
                            {column === 'status' && 'Stav'}
                            {column === 'contact' && 'Kontakt'}
                            {column === 'phone' && 'Telefon'}
                            {column === 'email' && 'Email'}
                            {column === 'bmsl' && 'BMSL'}
                            {column === 'forecast' && 'Forecast'}
                            {column === 'ca' && 'CA'}
                            {column === 'executive' && 'Vlastník firmy CRM'}
                            {column === 'prm' && 'PRM Vodafone'}
                            {column === 'closedDate' && 'Zobchodováno'}
                            {column === 'contractLength' && 'Délka smlouvy'}
                            {column === 'personType' && 'Typ osoby'}
                            {column === 'nextActionDate' && 'Datum další akce'}
                            {column === 'owner' && 'Vlastník'}
                            : "{value}"
                          </span>
                        ))}
                    </div>
                  </div>
                  <button
                    onClick={clearAllFilters}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 text-sm underline"
                  >
                    Vymazat všechny filtry
                  </button>
                </div>
              </div>
            )}
            
            {/* Toggle pro zobrazení/skrytí šarží */}
            {Object.keys(batchGroups).length > 0 && (
              <div className="mb-4 flex items-center gap-3">
                <button
                  onClick={() => setShowBatches(!showBatches)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    showBatches
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-500 hover:bg-gray-600 text-white'
                  }`}
                >
                  {showBatches ? '📂 Zobrazit bez šarží' : '📁 Zobrazit se šaržemi'}
                </button>
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {showBatches 
                    ? 'Kontakty jsou rozděleny podle šarží' 
                    : 'Všechny kontakty v jednom seznamu'
                  }
                </span>
              </div>
            )}
            
            {showBatches ? (
              Object.entries(batchGroups).map(([batchName, batchContacts]) => {
              const filteredBatch = filteredBatchGroupsMemo[batchName] || [];
              const currentPage = getBatchPage(batchName);
              const filteredCount = filteredBatch.length;
              const totalPages = Math.ceil(filteredCount / contactsPerPage) || 1;
              const paginatedContacts = sortContacts(filteredBatch).slice(
                (currentPage - 1) * contactsPerPage,
                currentPage * contactsPerPage
              );
              const hasActiveFilters = Object.values(columnFilters).some(filter => filter.trim() !== '');
              
              return (
                <div key={batchName} className={`border rounded-lg ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                  <div 
                    className={`p-4 border-b cursor-pointer flex justify-between items-center ${
                      isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => toggleBatchExpansion(batchName)}
                  >
                    <div className="flex items-center space-x-3">
                      {editingBatchName === batchName ? (
                        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={newBatchNameForRename}
                            onChange={(e) => setNewBatchNameForRename(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                renameBatch(batchName, newBatchNameForRename);
                              }
                            }}
                            className={`px-2 py-1 border rounded text-sm ${
                              isDarkMode
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="Nový název šarže"
                            autoFocus
                          />
                          <button
                            onClick={() => renameBatch(batchName, newBatchNameForRename)}
                            className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => {
                              setEditingBatchName(null);
                              setNewBatchNameForRename('');
                            }}
                            className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {batchName} 
                          {hasActiveFilters ? (
                            <span className="text-blue-600 dark:text-blue-400">
                              ({filteredCount} z {batchContacts.length} kontaktů)
                            </span>
                          ) : (
                            <span className="text-gray-600 dark:text-gray-300">({batchContacts.length} kontaktů)</span>
                          )}
                          {totalPages > 1 && (
                            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                              - Stránka {currentPage} z {totalPages}
                            </span>
                          )}
                        </h3>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Označit celou šarži (všechny stránky) */}
                      {(() => {
                        const allIds = (batchGroups[batchName] || []).map(c => c._id);
                        const allSelected = allIds.length > 0 && allIds.every(id => selectedContacts.has(id));
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              selectAllInBatch(batchName);
                            }}
                            className={`px-3 py-1 rounded text-sm transition-colors ${
                              allSelected
                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                : isDarkMode
                                  ? 'bg-indigo-900/40 border border-indigo-600 text-indigo-300 hover:bg-indigo-800/60'
                                  : 'bg-indigo-50 border border-indigo-300 text-indigo-700 hover:bg-indigo-100'
                            }`}
                            title={allSelected ? `Odznačit celou šarži (${allIds.length} kontaktů)` : `Označit celou šarži (${allIds.length} kontaktů)`}
                          >
                            {allSelected ? '☑ Odznačit vše' : '☐ Označit vše'}
                            {allIds.length > 0 && <span className="ml-1 text-xs opacity-70">({allIds.length})</span>}
                          </button>
                        );
                      })()}
                      {contactsUser && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            returnBatchToContacts(batchName);
                          }}
                          className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm"
                        >
                          Vrátit šarži
                        </button>
                      )}
                      {batchName !== 'Bez šarže' && editingBatchName !== batchName && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingBatchName(batchName);
                            setNewBatchNameForRename(batchName);
                          }}
                          className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm"
                          title="Přejmenovat šarži"
                        >
                          Přejmenovat
                        </button>
                      )}
                      {batchName !== 'Bez šarže' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBatch(batchName);
                          }}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm"
                          title="Smazat šarži (kontakty budou přesunuty do 'Bez šarže')"
                        >
                          Smazat šarži
                        </button>
                      )}
                      <span className="text-sm text-gray-700 dark:text-gray-200">
                        {expandedBatches.has(batchName) ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  
                  {expandedBatches.has(batchName) && (
                    <div className="p-4">
                      {/* Stránkování nahoře */}
                      {totalPages > 1 && (
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setBatchPage(batchName, Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                              className={`px-3 py-1 rounded text-sm ${
                                currentPage === 1
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                              }`}
                            >
                              ← Předchozí
                            </button>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Stránka {currentPage} z {totalPages} ({batchContacts.length} celkem)
                            </span>
                            <button
                              onClick={() => setBatchPage(batchName, Math.min(totalPages, currentPage + 1))}
                              disabled={currentPage === totalPages}
                              className={`px-3 py-1 rounded text-sm ${
                                currentPage === totalPages
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                              }`}
                            >
                              Další →
                            </button>
                          </div>
                          <div className="flex items-center space-x-2">
                            <label className="text-sm text-gray-700 dark:text-gray-200">Přejít na stránku:</label>
                            <select
                              value={currentPage}
                              onChange={(e) => setBatchPage(batchName, parseInt(e.target.value))}
                              className={`px-2 py-1 border rounded text-sm ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            >
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <option key={page} value={page}>
                                  {page}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      
                      <div className="overflow-x-auto">
                        <table className={`min-w-full ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          <thead>
                            <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <th className="text-left py-2 px-3 w-12">
                                <input
                                  type="checkbox"
                                  onChange={() => toggleBatchSelection(batchName)}
                                  checked={paginatedContacts.every(contact => selectedContacts.has(contact._id)) && paginatedContacts.length > 0}
                                  className="rounded"
                                  title="Označit/odznačit všechny na této stránce"
                                />
                              </th>
                              {columnOrder.map(columnName => {
                                if (!visibleColumns[columnName]) return null;
                                
                                const columnLabels = {
                                  name: 'Název',
                                  ico: 'IČO', 
                                  address: 'Adresa',
                                  kraj: 'Kraj',
                                  operator: 'Operátor',
                                  status: 'Stav',
                                  contact: 'Kontakt',
                                  phone: 'Telefon',
                                  email: 'Email',
                                  bmsl: 'BMSL',
                                  forecast: 'Forecast',
                                  ca: 'CA',
                                  executive: 'Vlastník firmy CRM',
                                  prm: 'PRM Vodafone',
                                  closedDate: 'Zobchodováno',
                                  contractLength: 'Délka smlouvy',
                                  personType: 'Typ osoby',
                                  nextActionDate: 'Datum další akce',
                                  owner: 'Vlastník',
                                  icoStatus: 'Stav IČO',
                                  signal: 'Signál LTE'
                                };
                                
                                return (
                                  <th key={columnName} className="text-left py-2 px-3">
                                    <button
                                      onClick={() => handleSort(columnName)}
                                      className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                    >
                                      {columnLabels[columnName]}
                                      {sortConfig.column === columnName && (
                                        <span className="text-xs">
                                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                      )}
                                    </button>
                                  </th>
                                );
                              })}
                              <th className="text-left py-2 px-3">Akce</th>
                            </tr>
                            {/* Řádek s filtry */}
                            <tr className={`border-b bg-gray-50 dark:bg-gray-800 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <th className="py-2 px-3">
                                <button
                                  onClick={clearAllFilters}
                                  className="text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                                  title="Vymazat všechny filtry"
                                >
                                  ✕
                                </button>
                              </th>
                              {columnOrder.map(columnName => {
                                if (!visibleColumns[columnName]) return null;
                                
                                const filterPlaceholders = {
                                  name: 'Filtr název... (N/A = prázdné)',
                                  ico: 'Filtr IČO... (N/A = prázdné)',
                                  address: 'Filtr adresa... (N/A = prázdné)',
                                  kraj: 'Filtr kraj... (N/A = prázdné)',
                                  operator: 'Filtr operátor... (N/A = prázdné)',
                                  status: 'Filtr stav... (N/A = prázdné)',
                                  contact: 'Filtr kontakt... (N/A = prázdné)',
                                  phone: 'Filtr telefon... (N/A = prázdné)',
                                  email: 'Filtr email... (N/A = prázdné)',
                                  bmsl: 'Filtr BMSL... (N/A = prázdné)',
                                  forecast: 'Filtr forecast... (N/A = prázdné)',
                                  ca: 'Filtr CA... (N/A = prázdné)',
                                  executive: 'Filtr vlastník... (N/A = prázdné)',
                                  prm: 'Filtr PRM... (N/A = prázdné)',
                                  closedDate: 'Filtr zobchodováno... (N/A = prázdné)',
                                  contractLength: 'Filtr délka smlouvy... (N/A = prázdné)',
                                  personType: 'Filtr typ osoby... (N/A = prázdné)',
                                  nextActionDate: 'Filtr datum další akce... (N/A = prázdné)',
                                  owner: 'Filtr vlastník... (N/A = prázdné)'
                                };
                                
                                return (
                                  <th key={columnName} className="py-2 px-3">
                                    <input
                                      type="text"
                                      placeholder={filterPlaceholders[columnName]}
                                      value={columnFilters[columnName]}
                                      onChange={(e) => updateColumnFilter(columnName, e.target.value)}
                                      className={`w-full px-2 py-1 text-xs border rounded ${
                                        isDarkMode
                                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                      }`}
                                    />
                                  </th>
                                );
                              })}
                              <th className="py-2 px-3">
                                {/* Prázdná buňka pro sloupec akcí */}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedContacts.map((contact) => (
                              <tr 
                                key={contact._id}
                                data-company-id={contact._id}
                                className={`border-b hover:bg-opacity-50 ${
                                  selectedContacts.has(contact._id) 
                                    ? isDarkMode 
                                      ? 'bg-blue-900 border-blue-700' 
                                      : 'bg-blue-100 border-blue-200'
                                    : isDarkMode 
                                      ? 'border-gray-700 hover:bg-gray-700' 
                                      : 'border-gray-200 hover:bg-gray-100'
                                }`}
                              >
                                <td className="py-2 px-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedContacts.has(contact._id)}
                                    onChange={() => toggleContactSelection(contact._id)}
                                    className="rounded"
                                  />
                                </td>
                                {columnOrder.map(columnName => {
                                  if (!visibleColumns[columnName]) return null;
                                  
                                  switch (columnName) {
                                    case 'name':
                                      return (
                                        <td key={columnName} className="py-2 px-3 font-medium">
                                          {contact.name}
                                        </td>
                                      );
                                    
                                    case 'ico':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.ico || ''}
                                        </td>
                                      );
                                    
                                    case 'address':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.address || ''}
                                        </td>
                                      );
                                    
                                    case 'kraj':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.kraj || ''}
                                        </td>
                                      );
                                    
                                    case 'operator':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {editingOperator === contact._id ? (
                                            <select
                                              value={contact.operator || ''}
                                              onChange={(e) => handleOperatorChange(contact._id, e.target.value)}
                                              onBlur={() => setEditingOperator(null)}
                                              className={`w-full px-2 py-1 text-sm border rounded ${
                                                isDarkMode
                                                  ? 'bg-gray-700 border-gray-600 text-white'
                                                  : 'bg-white border-gray-300 text-gray-900'
                                              }`}
                                              autoFocus
                                            >
                                              <option value="">Vyberte operátora</option>
                                              <option value="O2">O2</option>
                                              <option value="TM">T-Mobile</option>
                                              <option value="Vodafone">Vodafone</option>
                                            </select>
                                          ) : (
                                            <span 
                                              onClick={() => setEditingOperator(contact._id)}
                                              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                                              title="Kliknete pro změnu operátora"
                                            >
                                              {contact.operator || ''}
                                            </span>
                                          )}
                                        </td>
                                      );
                                    
                                    case 'status':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          <span className={`px-2 py-1 rounded text-sm ${
                                            contact.status === 'Hotovo' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                            contact.status === 'Nemá zájem' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                            contact.status === 'Vytvořit nabídku' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                            contact.status === 'Implementace' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                                            contact.status === 'Nový kontakt' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                          }`}>
                                            {contact.status || 'Nový kontakt'}
                                          </span>
                                        </td>
                                      );
                                    
                                    case 'contact':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.contacts && contact.contacts.length > 0 ? (
                                            <div className="text-sm">
                                              <div className="font-medium">{contact.contacts[0].name || 'Bez jména'}</div>
                                              {contact.contacts.length > 1 && (
                                                <div className="text-gray-500 dark:text-gray-400">
                                                  +{contact.contacts.length - 1} dalších
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            'Bez kontaktu'
                                          )}
                                        </td>
                                      );
                                    
                                    case 'phone':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.contacts && contact.contacts.length > 0 && contact.contacts[0].phone ? (
                                            <a href={`tel:${contact.contacts[0].phone.replace(/\s/g, '')}`} 
                                               className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                              {contact.contacts[0].phone}
                                            </a>
                                          ) : (
                                            'N/A'
                                          )}
                                        </td>
                                      );
                                    
                                    case 'email':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.contacts && contact.contacts.length > 0 && contact.contacts[0].email ? (
                                            <a href={`mailto:${contact.contacts[0].email}`} 
                                               className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                              {contact.contacts[0].email}
                                            </a>
                                          ) : (
                                            'N/A'
                                          )}
                                        </td>
                                      );
                                    
                                    case 'bmsl':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.bmsl ? `${contact.bmsl} Kč` : ''}
                                        </td>
                                      );
                                    
                                    case 'forecast':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.forecast ? `${contact.forecast}%` : ''}
                                        </td>
                                      );
                                    
                                    case 'ca':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.ca || ''}
                                        </td>
                                      );
                                    
                                    case 'executive':
                                      // Použít createdBy informace místo executive pole
                                      let executiveName = '';
                                      if (typeof contact.createdBy === 'object' && contact.createdBy?.name) {
                                        executiveName = contact.createdBy.name;
                                      } else if (typeof contact.createdBy === 'string') {
                                        // Pokud je createdBy jen ID, zkusit najít jméno v ownerInfo
                                        executiveName = contact.ownerInfo?.username || '';
                                      }
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {executiveName}
                                        </td>
                                      );
                                    
                                    case 'prm':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {editingPRM === contact._id ? (
                                            <input
                                              type="text"
                                              value={contact.prm || ''}
                                              onChange={(e) => {
                                                // Aktualizace hodnoty v reálném čase
                                                setContacts(prevContacts => 
                                                  prevContacts.map(c => 
                                                    c._id === contact._id 
                                                      ? { ...c, prm: e.target.value }
                                                      : c
                                                  )
                                                );
                                                setBatchGroups(prevGroups => {
                                                  const newGroups = { ...prevGroups };
                                                  Object.keys(newGroups).forEach(batchName => {
                                                    newGroups[batchName] = newGroups[batchName].map(c =>
                                                      c._id === contact._id 
                                                        ? { ...c, prm: e.target.value }
                                                        : c
                                                    );
                                                  });
                                                  return newGroups;
                                                });
                                              }}
                                              onBlur={() => {
                                                handlePRMChange(contact._id, contact.prm);
                                              }}
                                              onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                  handlePRMChange(contact._id, contact.prm);
                                                }
                                              }}
                                              className={`w-full px-2 py-1 text-sm border rounded ${
                                                isDarkMode
                                                  ? 'bg-gray-700 border-gray-600 text-white'
                                                  : 'bg-white border-gray-300 text-gray-900'
                                              }`}
                                              placeholder="Zadejte PRM"
                                              autoFocus
                                            />
                                          ) : (
                                            <span 
                                              onClick={() => setEditingPRM(contact._id)}
                                              className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                                              title="Klikněte pro změnu PRM Vodafone"
                                            >
                                              {contact.prm || 'Klikněte pro zadání'}
                                            </span>
                                          )}
                                        </td>
                                      );
                                    
                                    case 'closedDate':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.closedDate || ''}
                                        </td>
                                      );
                                    
                                    case 'contractLength':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.contractLength ? `${contact.contractLength} měsíců` : ''}
                                        </td>
                                      );
                                    
                                    case 'personType':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.personType || detectPersonType(contact.name)}
                                        </td>
                                      );
                                    
                                    case 'icoStatus':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.icoStatus ? (
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                              contact.icoStatus === 'aktivní'
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                                                : contact.icoStatus === 'pozastavené'
                                                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'
                                                  : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                                            }`}>{contact.icoStatus}</span>
                                          ) : ''}
                                        </td>
                                      );
                                    
                                    case 'signal':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.signal ? (
                                            <span
                                              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                                contact.signal.bars >= 4
                                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                                                  : contact.signal.bars >= 3
                                                    ? 'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-400'
                                                    : contact.signal.bars >= 2
                                                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400'
                                                      : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                                              }`}
                                              title={`${contact.signal.dbm?.toFixed(1)} dBm · ${contact.signal.quality}`}
                                            >
                                              {contact.signal.bars}/5
                                            </span>
                                          ) : ''}
                                        </td>
                                      );
                                    
                                    case 'nextActionDate':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          {contact.nextActionDate ? new Date(contact.nextActionDate).toLocaleDateString('cs-CZ') : ''}
                                        </td>
                                      );
                                    
                                    case 'owner':
                                      return (
                                        <td key={columnName} className="py-2 px-3">
                                          <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {contact.ownerInfo?.username || ''}
                                          </span>
                                        </td>
                                      );
                                    
                                    default:
                                      return null;
                                  }
                                })}
                                <td className="py-2 px-3">
                                  <button
                                    onClick={() => window.open(`/#/companies/${contact._id}`, '_blank', 'noopener,noreferrer')}
                                    className={`px-2 py-1 text-xs rounded ${
                                      isDarkMode
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                                    }`}
                                  >
                                    Detail
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Stránkování dole */}
                      {totalPages > 1 && (
                        <div className="flex justify-center items-center mt-4 space-x-2">
                          <button
                            onClick={() => setBatchPage(batchName, Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded text-sm ${
                              currentPage === 1
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                          >
                            ← Předchozí
                          </button>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {currentPage} / {totalPages}
                          </span>
                          <button
                            onClick={() => setBatchPage(batchName, Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded text-sm ${
                              currentPage === totalPages
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                                : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                          >
                            Další →
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
            ) : (
              // Zobrazení všech kontaktů bez rozdělení do šarží
              <div className={`border rounded-lg ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-white'}`}>
                <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Všechny kontakty
                    <span className="text-gray-600 dark:text-gray-300 ml-2">
                      ({filteredContactsAll.length} kontaktů)
                    </span>
                  </h3>
                </div>
                
                {filteredContactsAll.length > 0 && (
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className={`min-w-full ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <thead>
                          <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            <th className="text-left py-2 px-3 w-12">
                              <input
                                type="checkbox"
                                onChange={(e) => {
                                  const filtered = filteredContactsAll;
                                  if (e.target.checked) {
                                    setSelectedContacts(new Set(filtered.map(c => c._id)));
                                  } else {
                                    setSelectedContacts(new Set());
                                  }
                                }}
                                checked={contacts.length > 0 && filteredContactsAll.every(c => selectedContacts.has(c._id))}
                                className="rounded"
                              />
                            </th>
                            {columnOrder.map(columnName => {
                              if (!visibleColumns[columnName]) return null;
                              const columnLabels = {
                                name: 'Název',
                                ico: 'IČO',
                                address: 'Adresa',
                                kraj: 'Kraj',
                                operator: 'Operátor',
                                status: 'Stav',
                                contact: 'Kontakt',
                                phone: 'Telefon',
                                email: 'Email',
                                bmsl: 'BMSL',
                                forecast: 'Forecast',
                                ca: 'CA',
                                executive: 'Vlastník firmy CRM',
                                prm: 'PRM Vodafone',
                                closedDate: 'Zobchodováno',
                                contractLength: 'Délka smlouvy',
                                personType: 'Typ osoby',
                                nextActionDate: 'Datum další akce',
                                owner: 'Vlastník',
                                icoStatus: 'Stav IČO',
                                signal: 'Signál LTE'
                              };
                              return (
                                <th key={columnName} className="text-left py-2 px-3">
                                  <button
                                    onClick={() => handleSort(columnName)}
                                    className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                  >
                                    {columnLabels[columnName]}
                                    {sortConfig.key === columnName && (
                                      <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                    )}
                                  </button>
                                </th>
                              );
                            })}
                            <th className="text-left py-2 px-3">Akce</th>
                          </tr>
                          {/* Řádek s filtry */}
                          <tr className={`border-b bg-gray-50 dark:bg-gray-800 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            <th className="py-2 px-3">
                              <button
                                onClick={clearAllFilters}
                                className="text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                                title="Vymazat všechny filtry"
                              >
                                ✕
                              </button>
                            </th>
                            {columnOrder.map(columnName => {
                              if (!visibleColumns[columnName]) return null;
                              
                              const filterPlaceholders = {
                                name: 'Filtr název... (čárka pro více)',
                                ico: 'Filtr IČO... (čárka pro více)',
                                address: 'Filtr adresa... (čárka pro více)',
                                kraj: 'Filtr kraj... (čárka pro více)',
                                operator: 'Filtr operátor... (čárka pro více)',
                                status: 'Filtr stav... (čárka pro více)',
                                contact: 'Filtr kontakt... (čárka pro více)',
                                phone: 'Filtr telefon... (čárka pro více)',
                                email: 'Filtr email... (čárka pro více)',
                                bmsl: 'Filtr BMSL... (>, <, rozsah)',
                                forecast: 'Filtr forecast... (>, <, rozsah)',
                                ca: 'Filtr CA... (čárka pro více)',
                                executive: 'Filtr vlastník... (čárka pro více)',
                                prm: 'Filtr PRM... (čárka pro více)',
                                closedDate: 'Filtr zobchodováno... (čárka pro více)',
                                contractLength: 'Filtr délka smlouvy... (>, <)',
                                personType: 'Filtr typ osoby... (čárka pro více)',
                                nextActionDate: 'Filtr datum... (čárka pro více)',
                                owner: 'Filtr vlastník... (čárka pro více)'
                              };
                              
                              return (
                                <th key={columnName} className="py-2 px-3">
                                  <input
                                    type="text"
                                    placeholder={filterPlaceholders[columnName]}
                                    value={columnFilters[columnName]}
                                    onChange={(e) => updateColumnFilter(columnName, e.target.value)}
                                    className={`w-full px-2 py-1 text-xs border rounded ${
                                      isDarkMode
                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                    }`}
                                  />
                                </th>
                              );
                            })}
                            <th className="py-2 px-3">
                              {/* Prázdná buňka pro sloupec akcí */}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredContactsAll.map((contact) => (
                            <tr 
                              key={contact._id}
                              data-company-id={contact._id}
                              className={`border-b hover:bg-opacity-50 ${
                                selectedContacts.has(contact._id) 
                                  ? isDarkMode 
                                    ? 'bg-blue-900 border-blue-700' 
                                    : 'bg-blue-100 border-blue-200'
                                  : isDarkMode 
                                    ? 'border-gray-700 hover:bg-gray-700' 
                                    : 'border-gray-200 hover:bg-gray-100'
                              }`}
                            >
                              <td className="py-2 px-3">
                                <input
                                  type="checkbox"
                                  checked={selectedContacts.has(contact._id)}
                                  onChange={() => toggleContactSelection(contact._id)}
                                  className="rounded"
                                />
                              </td>
                              {columnOrder.map(columnName => {
                                if (!visibleColumns[columnName]) return null;
                                
                                switch (columnName) {
                                  case 'name':
                                    return (
                                      <td key={columnName} className="py-2 px-3 font-medium">
                                        {contact.name}
                                      </td>
                                    );
                                  
                                  case 'ico':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.ico || ''}
                                      </td>
                                    );
                                  
                                  case 'address':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.address || ''}
                                      </td>
                                    );
                                  
                                  case 'kraj':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.kraj || ''}
                                      </td>
                                    );
                                  
                                  case 'operator':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {editingOperator === contact._id ? (
                                          <select
                                            value={contact.operator || ''}
                                            onChange={(e) => handleOperatorChange(contact._id, e.target.value)}
                                            onBlur={() => setEditingOperator(null)}
                                            className={`w-full px-2 py-1 text-sm border rounded ${
                                              isDarkMode
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                            autoFocus
                                          >
                                            <option value="">Vyberte operátora</option>
                                            <option value="O2">O2</option>
                                            <option value="TM">T-Mobile</option>
                                            <option value="Vodafone">Vodafone</option>
                                          </select>
                                        ) : (
                                          <span 
                                            onClick={() => setEditingOperator(contact._id)}
                                            className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                                            title="Kliknete pro změnu operátora"
                                          >
                                            {contact.operator || ''}
                                          </span>
                                        )}
                                      </td>
                                    );
                                  
                                  case 'status':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        <span className={`px-2 py-1 rounded text-sm ${
                                          contact.status === 'Hotovo' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                          contact.status === 'Nemá zájem' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                          contact.status === 'Vytvořit nabídku' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                          contact.status === 'Implementace' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                                          contact.status === 'Nový kontakt' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        }`}>
                                          {contact.status || 'Nový kontakt'}
                                        </span>
                                      </td>
                                    );
                                  
                                  case 'contact':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.contacts && contact.contacts.length > 0 ? (
                                          <div className="text-sm">
                                            <div className="font-medium">{contact.contacts[0].name || 'Bez jména'}</div>
                                            {contact.contacts.length > 1 && (
                                              <div className="text-gray-500 dark:text-gray-400">
                                                +{contact.contacts.length - 1} dalších
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          'Bez kontaktu'
                                        )}
                                      </td>
                                    );
                                  
                                  case 'phone':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.contacts && contact.contacts.length > 0 && contact.contacts[0].phone ? (
                                          <a href={`tel:${contact.contacts[0].phone.replace(/\s/g, '')}`} 
                                             className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                            {contact.contacts[0].phone}
                                          </a>
                                        ) : (
                                          'N/A'
                                        )}
                                      </td>
                                    );
                                  
                                  case 'email':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.contacts && contact.contacts.length > 0 && contact.contacts[0].email ? (
                                          <a href={`mailto:${contact.contacts[0].email}`} 
                                             className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                            {contact.contacts[0].email}
                                          </a>
                                        ) : (
                                          'N/A'
                                        )}
                                      </td>
                                    );
                                  
                                  case 'bmsl':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.bmsl ? `${contact.bmsl} Kč` : ''}
                                      </td>
                                    );
                                  
                                  case 'forecast':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.forecast ? `${contact.forecast}%` : ''}
                                      </td>
                                    );
                                  
                                  case 'ca':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.ca || ''}
                                      </td>
                                    );
                                  
                                  case 'executive':
                                    let executiveName = '';
                                    if (typeof contact.createdBy === 'object' && contact.createdBy?.name) {
                                      executiveName = contact.createdBy.name;
                                    } else if (typeof contact.createdBy === 'string') {
                                      executiveName = contact.ownerInfo?.username || '';
                                    }
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {executiveName}
                                      </td>
                                    );
                                  
                                  case 'prm':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {editingPRM === contact._id ? (
                                          <input
                                            type="text"
                                            value={contact.prm || ''}
                                            onChange={(e) => {
                                              setContacts(prevContacts => 
                                                prevContacts.map(c => 
                                                  c._id === contact._id 
                                                    ? { ...c, prm: e.target.value }
                                                    : c
                                                )
                                              );
                                              setBatchGroups(prevGroups => {
                                                const newGroups = { ...prevGroups };
                                                Object.keys(newGroups).forEach(batchName => {
                                                  newGroups[batchName] = newGroups[batchName].map(c =>
                                                    c._id === contact._id 
                                                      ? { ...c, prm: e.target.value }
                                                      : c
                                                  );
                                                });
                                                return newGroups;
                                              });
                                            }}
                                            onBlur={() => {
                                              handlePRMChange(contact._id, contact.prm);
                                            }}
                                            onKeyPress={(e) => {
                                              if (e.key === 'Enter') {
                                                handlePRMChange(contact._id, contact.prm);
                                              }
                                            }}
                                            className={`w-full px-2 py-1 text-sm border rounded ${
                                              isDarkMode
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                            placeholder="Zadejte PRM"
                                            autoFocus
                                          />
                                        ) : (
                                          <span 
                                            onClick={() => setEditingPRM(contact._id)}
                                            className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded"
                                            title="Klikněte pro změnu PRM Vodafone"
                                          >
                                            {contact.prm || 'Klikněte pro zadání'}
                                          </span>
                                        )}
                                      </td>
                                    );
                                  
                                  case 'closedDate':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.closedDate || ''}
                                      </td>
                                    );
                                  
                                  case 'contractLength':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.contractLength ? `${contact.contractLength} měsíců` : ''}
                                      </td>
                                    );
                                  
                                  case 'personType':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.personType || detectPersonType(contact.name)}
                                      </td>
                                    );
                                  
                                  case 'icoStatus':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.icoStatus ? (
                                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                            contact.icoStatus === 'aktivní'
                                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                                              : contact.icoStatus === 'pozastavené'
                                                ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400'
                                                : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                                          }`}>{contact.icoStatus}</span>
                                        ) : ''}
                                      </td>
                                    );
                                  
                                  case 'signal':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.signal ? (
                                          <span
                                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                              contact.signal.bars >= 4
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                                                : contact.signal.bars >= 3
                                                  ? 'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-400'
                                                  : contact.signal.bars >= 2
                                                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400'
                                                    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                                            }`}
                                            title={`${contact.signal.dbm?.toFixed(1)} dBm · ${contact.signal.quality}`}
                                          >
                                            {contact.signal.bars}/5
                                          </span>
                                        ) : ''}
                                      </td>
                                    );
                                  
                                  case 'nextActionDate':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        {contact.nextActionDate ? new Date(contact.nextActionDate).toLocaleDateString('cs-CZ') : ''}
                                      </td>
                                    );
                                  
                                  case 'owner':
                                    return (
                                      <td key={columnName} className="py-2 px-3">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                          {contact.ownerInfo?.username || ''}
                                        </span>
                                      </td>
                                    );
                                  
                                  default:
                                    return null;
                                }
                              })}
                              <td className="py-2 px-3">
                                <button
                                  onClick={() => window.open(`/#/companies/${contact._id}`, '_blank', 'noopener,noreferrer')}
                                  className={`px-2 py-1 text-xs rounded ${
                                    isDarkMode
                                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                                  }`}
                                >
                                  Detail
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {selectedUser || selectedUsers.length > 0 ? 
              (selectedUser === 'ALL_USERS' ? 'Žádní uživatelé nemají kontakty.' : 
               selectedUsers.length > 0 ? 'Vybraní uživatelé nemají žádné kontakty.' : 
               'Vybraný uživatel nemá žádné kontakty.') : 
              'Vyberte uživatele pro zobrazení kontaktů.'}
          </div>
        )}
      </div>
      
      {/* ImportWizard modal */}
      {showImportWizard && (
        <ImportWizard
          onImportComplete={handleImportComplete}
          onClose={() => {
            setShowImportWizard(false);
            setImportUpdateMode(false);
          }}
          updateMode={importUpdateMode}
          userId={currentUserId}
          isDarkMode={isDarkMode}
        />
      )}
      
      {/* OperatorVerification modal */}
      {showOperatorVerification && (
        <OperatorVerification
          onClose={() => setShowOperatorVerification(false)}
          isDarkMode={isDarkMode}
          companies={contacts}
        />
      )}
      
      {/* CAVerification modal */}
      {showCAVerification && (
        <CAVerification
          onClose={() => setShowCAVerification(false)}
          isDarkMode={isDarkMode}
          companies={contacts}
          currentUserId={currentUserId}
        />
      )}
      
      {/* SimpleCAImport modal */}
      {showSimpleCAImport && (
        <SimpleCAImport
          onClose={() => setShowSimpleCAImport(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Oprava IČ dle MSISDN modal */}
      {showMsisdnIcoFix && (
        <MsisdnIcoFixModal
          onClose={() => setShowMsisdnIcoFix(false)}
          isDarkMode={isDarkMode}
          currentUserId={currentUserId}
          companies={contacts}
          onUpdated={() => {
            if (selectedUser === 'ALL_USERS') {
              loadAllUsersCompanies();
            } else if (selectedUser) {
              loadUserCompaniesAuto(selectedUser, 1);
            } else if (selectedUsers.length > 0) {
              setSelectedUsers(prev => [...prev]);
            }
          }}
        />
      )}
      
      {/* IcoOwnerChecker modal */}
      {showIcoOwnerChecker && (
        <IcoOwnerChecker
          onClose={() => setShowIcoOwnerChecker(false)}
          isDarkMode={isDarkMode}
        />
      )}

      {/* BulkContactImport modal */}
      {showBulkContactImport && (
        <BulkContactImport
          onClose={() => setShowBulkContactImport(false)}
          isDarkMode={isDarkMode}
          onImportComplete={() => {
            setShowBulkContactImport(false);
            // Znovu načti kontakty aby se projevily změny
            if (selectedUser === 'ALL_USERS') {
              loadAllUsersCompanies();
            } else if (selectedUser) {
              loadUserCompaniesAuto(selectedUser, 1);
            } else if (selectedUsers.length > 0) {
              // Re-trigger multi-select load
              setSelectedUsers(prev => [...prev]);
            }
          }}
        />
      )}
      
      {/* BulkExport modal */}
      {showBulkExport && (
        <BulkExport
          onClose={() => setShowBulkExport(false)}
          isDarkMode={isDarkMode}
          batchGroups={batchGroups}
          allContacts={contacts}
          filteredContacts={filteredContactsAll}
          visibleColumns={visibleColumns}
          columnOrder={columnOrder}
        />
      )}

      {/* Multi-select uživatelů modal */}
      {showUserSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-2xl w-full max-h-[90vh] overflow-hidden rounded-lg shadow-2xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Header */}
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Vyberte uživatele pro zobrazení kontaktů
              </h2>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Zaškrtněte uživatele, jejichž kontakty chcete zobrazit
              </p>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-96">
              {/* Vyhledávání */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="🔍 Hledat uživatele..."
                  value={selectorSearch}
                  onChange={(e) => setSelectorSearch(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              {/* Rychlé akce */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={selectAllUsers}
                  className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition"
                >
                  Vybrat vše
                </button>
                <button
                  onClick={deselectAllUsers}
                  className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded transition"
                >
                  Zrušit výběr
                </button>
              </div>

              {/* Uživatelé seskupení podle role */}
              {groupUsersByRole(users, selectorSearch).map(({ role, label, users: roleUsers }) => (
                <div key={role} className="mb-4">
                  <div className={`flex items-center gap-2 px-2 py-1.5 rounded-md mb-2 ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <span className={`text-xs font-bold uppercase tracking-wide ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {label}
                    </span>
                    <span className={`text-xs ml-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {roleUsers.filter(u => selectedUsers.includes(u._id)).length}/{roleUsers.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const allSelected = roleUsers.every(u => selectedUsers.includes(u._id));
                        if (allSelected) {
                          setSelectedUsers(prev => prev.filter(id => !roleUsers.some(u => u._id === id)));
                        } else {
                          setSelectedUsers(prev => [...new Set([...prev, ...roleUsers.map(u => u._id)])]);
                        }
                      }}
                      className={`text-xs px-2 py-0.5 rounded ${
                        isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-white hover:bg-gray-200 text-gray-700 border border-gray-300'
                      }`}
                    >
                      {roleUsers.every(u => selectedUsers.includes(u._id)) ? 'Odebrat' : 'Vybrat'}
                    </button>
                  </div>
                  <div className="space-y-1 pl-2">
                    {roleUsers.map(user => (
                      <label
                        key={user._id}
                        className={`flex items-center p-2.5 rounded-lg cursor-pointer border transition-colors ${
                          selectedUsers.includes(user._id)
                            ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600'
                            : isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user._id)}
                          onChange={(e) => handleUserSelection(user._id, e.target.checked)}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {user.username}
                          </div>
                          <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {user.email}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {selectorSearch && groupUsersByRole(users, selectorSearch).length === 0 && (
                <div className={`text-center py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Žádný uživatel nenalezen pro „{selectorSearch}"
                </div>
              )}

              {selectedUsers.length > 0 && (
                <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Vybráno uživatelů: <strong>{selectedUsers.length}</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`p-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-3`}>
              <button
                onClick={() => {
                  setShowUserSelector(false);
                  setSelectedUsers([]);
                }}
                className={`px-4 py-2 border rounded-lg font-medium transition ${
                  isDarkMode 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Zrušit
              </button>
              <button
                onClick={confirmUserSelection}
                disabled={selectedUsers.length === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition"
              >
                Potvrdit ({selectedUsers.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hodnocení šarží modal */}
      {showRatings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-5 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                ⭐ Hodnocení šarží
              </h2>
              <button
                onClick={() => { setShowRatings(false); setExpandedRatingBatch(null); }}
                className={`text-2xl leading-none transition ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
              >
                ×
              </button>
            </div>

            {/* Tělo */}
            <div className="overflow-y-auto flex-1 p-4 space-y-2">
              {Object.keys(batchGroups).length === 0 ? (
                <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Žádné šarže k zobrazení. Nejprve načtěte kontakty.
                </p>
              ) : (
                Object.entries(batchGroups)
                  .sort((a, b) => {
                    // Seřadit dle hodnocení aktuálního uživatele, pak dle průměru
                    const myA = batchRatings[a[0]]?.[currentUserId]?.stars || 0;
                    const myB = batchRatings[b[0]]?.[currentUserId]?.stars || 0;
                    if (myB !== myA) return myB - myA;
                    const avgRating = (ratings) => {
                      const vals = Object.values(ratings || {}).map(r => r.stars || 0);
                      return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
                    };
                    return avgRating(batchRatings[b[0]]) - avgRating(batchRatings[a[0]]);
                  })
                  .map(([batchName, batchContacts]) => {
                    const myStars = batchRatings[batchName]?.[currentUserId]?.stars || 0;
                    const userRatings = Object.values(batchRatings[batchName] || {});
                    const avgStars = userRatings.length
                      ? (userRatings.reduce((s, r) => s + (r.stars || 0), 0) / userRatings.length).toFixed(1)
                      : null;
                    return (
                    <div key={batchName} className={`rounded-lg border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      {/* Řádek šarže */}
                      <div
                        className={`flex items-center gap-3 p-3 cursor-pointer rounded-lg transition ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                        onClick={() => setExpandedRatingBatch(expandedRatingBatch === batchName ? null : batchName)}
                      >
                        {/* Hvězdičky – moje hodnocení */}
                        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                          {[1,2,3,4,5].map(star => (
                            <button
                              key={star}
                              onClick={() => setRating(batchName, myStars === star ? 0 : star)}
                              title={`${star} hvězdiček`}
                              className="text-xl leading-none transition-transform hover:scale-125 focus:outline-none"
                              style={{ color: star <= myStars ? '#f59e0b' : (isDarkMode ? '#4b5563' : '#d1d5db') }}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                        {/* Průměr ostatních */}
                        {avgStars && (
                          <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                            title={`Průměr ${userRatings.length} hodnocení`}>
                            ⌀&nbsp;{avgStars}
                          </span>
                        )}
                        {/* Název */}
                        <span className={`flex-1 font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {batchName}
                        </span>
                        {/* Počet kontaktů */}
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'}`}>
                          {batchContacts.length} kontaktů
                        </span>
                        {/* Expand */}
                        <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                          {expandedRatingBatch === batchName ? '▲' : '▼'}
                        </span>
                      </div>

                      {/* Rozbalená sekce */}
                      {expandedRatingBatch === batchName && (
                        <div className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          {/* Hodnocení uživatelů */}
                          {Object.keys(batchRatings[batchName] || {}).length > 0 && (
                            <div className={`px-3 py-2 border-b ${isDarkMode ? 'border-gray-600 bg-gray-750' : 'border-gray-100 bg-gray-50'}`}>
                              <p className={`text-xs font-semibold mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Hodnocení uživatelů:</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(batchRatings[batchName]).map(([uid, rating]) => {
                                  const realName = users.find(u => u._id === uid)?.username || rating.userName || 'Uživatel';
                                  return (
                                  <div key={uid} className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white border border-gray-200 text-gray-700'}`}>
                                    <span className="font-medium">{realName}</span>
                                    <span className="flex">
                                      {[1,2,3,4,5].map(s => (
                                        <span key={s} style={{ color: s <= (rating.stars || 0) ? '#f59e0b' : (isDarkMode ? '#4b5563' : '#d1d5db') }}>★</span>
                                      ))}
                                    </span>
                                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{rating.stars}/5</span>
                                    <button
                                      onClick={() => deleteUserRating(batchName, uid)}
                                      title={`Smazat hodnocení uživatele ${realName}`}
                                      className={`ml-1 text-sm leading-none transition hover:scale-110 focus:outline-none ${isDarkMode ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                                    >
                                      ×
                                    </button>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {/* Tabulka kontaktů */}
                          <div className="max-h-64 overflow-y-auto">
                          <table className={`w-full text-xs ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            <thead>
                              <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                <th className="text-left px-3 py-2 font-semibold">Název firmy</th>
                                <th className="text-left px-3 py-2 font-semibold">IČO</th>
                                <th className="text-left px-3 py-2 font-semibold">Adresa</th>
                                <th className="text-left px-3 py-2 font-semibold">Operátor</th>
                                <th className="text-left px-3 py-2 font-semibold">Stav</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batchContacts.map(contact => (
                                <tr key={contact._id} className={`border-t ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}>
                                  <td className="px-3 py-1.5 font-medium">{contact.name || '—'}</td>
                                  <td className="px-3 py-1.5">{contact.ico || '—'}</td>
                                  <td className="px-3 py-1.5">{contact.address || '—'}</td>
                                  <td className="px-3 py-1.5">{contact.operator || '—'}</td>
                                  <td className="px-3 py-1.5">{contact.status || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })
              )}
            </div>

            {/* Footer */}
            <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => { setShowRatings(false); setExpandedRatingBatch(null); }}
                className={`w-full px-4 py-2 rounded-lg font-semibold transition ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DOČASNÝ MODAL - Čištění kontaktů */}
      {showContactCleaner && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-6xl w-full max-h-[90vh] overflow-hidden rounded-lg shadow-2xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className={`text-xl font-bold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    🧹 Náhled čištění kontaktů
                  </h2>
                  <p className={`text-sm mt-1 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Odstraní se vše od mezery před pomlčkou včetně (např. "Jan Novák - 601 123 456" → "Jan Novák")
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowContactCleaner(false);
                    setContactCleanPreview([]);
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  ✕ Zavřít
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {contactCleanPreview.length > 0 ? (
                <>
                  <div className={`mb-4 p-4 rounded ${
                    isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'
                  } border`}>
                    <p className={`font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                      Bude vyčištěno {contactCleanPreview.length} kontaktů:
                    </p>
                  </div>

                  <div className="space-y-3">
                    {contactCleanPreview.map((item, index) => (
                      <div
                        key={item.id}
                        className={`p-4 rounded border ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className={`font-medium mb-2 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {index + 1}. {item.name}
                        </div>
                        
                        <div className={`text-sm mb-3 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Pole: <span className="font-mono">{item.fieldName}</span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${
                              isDarkMode ? 'text-red-300' : 'text-red-600'
                            }`}>
                              Před čištěním:
                            </label>
                            <div className={`p-2 rounded text-sm ${
                              isDarkMode
                                ? 'bg-red-900/30 border-red-700 text-red-200'
                                : 'bg-red-50 border-red-200 text-red-800'
                            } border`}>
                              "{item.original}"
                            </div>
                          </div>
                          
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${
                              isDarkMode ? 'text-green-300' : 'text-green-600'
                            }`}>
                              Po vyčištění:
                            </label>
                            <div className={`p-2 rounded text-sm ${
                              isDarkMode
                                ? 'bg-green-900/30 border-green-700 text-green-200'
                                : 'bg-green-50 border-green-200 text-green-800'
                            } border`}>
                              "{item.cleaned}"
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className={`text-center py-8 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Žádné kontakty k vyčištění
                </div>
              )}
            </div>

            {/* Footer */}
            {contactCleanPreview.length > 0 && (
              <div className={`px-6 py-4 border-t ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className={`text-sm ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    ⚠️ Tato akce je nevratná! Před pokračováním si ověřte náhled změn.
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowContactCleaner(false);
                        setContactCleanPreview([]);
                      }}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Zrušit
                    </button>
                    <button
                      onClick={applyContactCleaning}
                      className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm font-medium"
                    >
                      ✅ Aplikovat čištění ({contactCleanPreview.length} kontaktů)
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading screen pro čištění kontaktů */}
      {isCleaningContacts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`rounded-lg p-8 flex flex-col items-center shadow-2xl min-w-80 ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mb-4"></div>
            <p className={`text-lg font-semibold mb-2 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-700'
            }`}>
              {cleaningProgress.status}
            </p>
            
            {cleaningProgress.total > 0 && (
              <>
                <div className={`w-full rounded-full h-2.5 mb-2 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  <div 
                    className="bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${(cleaningProgress.current / cleaningProgress.total) * 100}%` }}
                  ></div>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {Math.round((cleaningProgress.current / cleaningProgress.total) * 100)}% 
                  ({cleaningProgress.current} z {cleaningProgress.total})
                </p>
              </>
            )}
            
            <p className={`text-xs mt-2 text-center ${
              isDarkMode ? 'text-gray-500' : 'text-gray-500'
            }`}>
              Čištění kontaktů probíhá...
            </p>
          </div>
        </div>
      )}

      {/* MODAL - Otočení datumů */}
      {showDateFlipper && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-lg shadow-2xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    📅 Náhled otočení datumů
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Den a měsíc budou vzájemně prohozeny (např. 5.1.2026 → 1.5.2026)
                  </p>
                </div>
                <button
                  onClick={() => { setShowDateFlipper(false); setDateFlipPreview([]); }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  ✕ Zavřít
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className={`mb-4 p-4 rounded border ${
                isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'
              }`}>
                <p className={`font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                  Bude upraveno {dateFlipPreview.length} firem:
                </p>
              </div>

              <div className="space-y-3">
                {dateFlipPreview.map((item, index) => (
                  <div key={item.id} className={`p-4 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {index + 1}. {item.name}
                    </div>
                    <div className="space-y-2">
                      {item.fields.map((field) => (
                        <div key={field.fieldName} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {field.label}:
                          </span>
                          <div className={`p-2 rounded text-sm border ${
                            isDarkMode ? 'bg-red-900/30 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-800'
                          }`}>
                            {field.originalDisplay}
                          </div>
                          <div className={`p-2 rounded text-sm border ${
                            isDarkMode ? 'bg-green-900/30 border-green-700 text-green-200' : 'bg-green-50 border-green-200 text-green-800'
                          }`}>
                            {field.flippedDisplay}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            {dateFlipPreview.length > 0 && (
              <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    ⚠️ Tato akce je nevratná! Před pokračováním si ověřte náhled změn.
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowDateFlipper(false); setDateFlipPreview([]); }}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Zrušit
                    </button>
                    <button
                      onClick={applyDateFlip}
                      className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium"
                    >
                      ✅ Otočit datumy ({dateFlipPreview.length} firem)
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading screen pro otočení datumů */}
      {isFlippingDates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`rounded-lg p-8 flex flex-col items-center shadow-2xl min-w-80 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
            <p className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {dateFlipProgress.status}
            </p>
            {dateFlipProgress.total > 0 && (
              <>
                <div className={`w-full rounded-full h-2.5 mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${(dateFlipProgress.current / dateFlipProgress.total) * 100}%` }}
                  ></div>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {Math.round((dateFlipProgress.current / dateFlipProgress.total) * 100)}%
                  ({dateFlipProgress.current} z {dateFlipProgress.total})
                </p>
              </>
            )}
            <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Otáčení datumů probíhá...
            </p>
          </div>
        </div>
      )}

      {/* MODAL - Oprava telefonů */}
      {showPhoneFixer && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-4xl w-full max-h-[90vh] overflow-hidden rounded-lg shadow-2xl ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    📞 Náhled opravy telefonů
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    9-místná česká čísla budou naformátována na "+420 XXX XXX XXX"
                  </p>
                </div>
                <button
                  onClick={() => { setShowPhoneFixer(false); setPhoneFixPreview([]); }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  ✕ Zavřít
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className={`mb-4 p-4 rounded border ${
                isDarkMode ? 'bg-teal-900/30 border-teal-700' : 'bg-teal-50 border-teal-200'
              }`}>
                <p className={`font-medium ${isDarkMode ? 'text-teal-200' : 'text-teal-800'}`}>
                  Bude opraveno {phoneFixPreview.length} firem:
                </p>
              </div>

              <div className="space-y-3">
                {phoneFixPreview.map((item, index) => (
                  <div key={item.id} className={`p-4 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {index + 1}. {item.name}
                    </div>
                    <div className="space-y-2">
                      {item.phones.map((phone) => (
                        <div key={phone.contactIndex} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {phone.contactName}:
                          </span>
                          <div className={`p-2 rounded text-sm border font-mono ${
                            isDarkMode ? 'bg-red-900/30 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-800'
                          }`}>
                            {phone.original}
                          </div>
                          <div className={`p-2 rounded text-sm border font-mono ${
                            isDarkMode ? 'bg-green-900/30 border-green-700 text-green-200' : 'bg-green-50 border-green-200 text-green-800'
                          }`}>
                            {phone.formatted}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            {phoneFixPreview.length > 0 && (
              <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    ⚠️ Tato akce je nevratná! Před pokračováním si ověřte náhled změn.
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowPhoneFixer(false); setPhoneFixPreview([]); }}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Zrušit
                    </button>
                    <button
                      onClick={applyPhoneFix}
                      className="px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded text-sm font-medium"
                    >
                      ✅ Opravit telefony ({phoneFixPreview.length} firem)
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading screen pro opravu telefonů */}
      {isFixingPhones && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`rounded-lg p-8 flex flex-col items-center shadow-2xl min-w-80 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent mb-4"></div>
            <p className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {phoneFixProgress.status}
            </p>
            {phoneFixProgress.total > 0 && (
              <>
                <div className={`w-full rounded-full h-2.5 mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-teal-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${(phoneFixProgress.current / phoneFixProgress.total) * 100}%` }}
                  ></div>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {Math.round((phoneFixProgress.current / phoneFixProgress.total) * 100)}%
                  ({phoneFixProgress.current} z {phoneFixProgress.total})
                </p>
              </>
            )}
            <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Oprava telefonů probíhá...
            </p>
          </div>
        </div>
      )}

      {/* Loading screen pro aktualizaci adres z ARES */}
      {isUpdatingAddresses && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`rounded-lg p-8 flex flex-col items-center shadow-2xl min-w-80 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent mb-4"></div>
            <p className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {addressUpdateProgress.status || 'Načítám data z ARES...'}
            </p>
            {addressUpdateProgress.total > 0 && (
              <>
                <div className={`w-full rounded-full h-2.5 mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-indigo-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${(addressUpdateProgress.current / addressUpdateProgress.total) * 100}%` }}
                  ></div>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {Math.round((addressUpdateProgress.current / addressUpdateProgress.total) * 100)}%
                  ({addressUpdateProgress.current} z {addressUpdateProgress.total})
                </p>
              </>
            )}
            <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Aktualizace adres z ARES probíhá...
            </p>
          </div>
        </div>
      )}

      {isBulkSignal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`rounded-lg p-8 flex flex-col items-center shadow-2xl min-w-80 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent mb-4"></div>
            <p className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {bulkSignalProgress.status || 'Ověřuji signál...'}
            </p>
            {bulkSignalProgress.total > 0 && (
              <>
                <div className={`w-full rounded-full h-2.5 mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-red-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${(bulkSignalProgress.current / bulkSignalProgress.total) * 100}%` }}
                  ></div>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {Math.round((bulkSignalProgress.current / bulkSignalProgress.total) * 100)}%
                  ({bulkSignalProgress.current} z {bulkSignalProgress.total})
                </p>
              </>
            )}
            <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Data z ČTÚ VPortal · Vodafone LTE
            </p>
          </div>
        </div>
      )}

      {/* Automatické ověření na pozadí */}
      {showAutoVerification && (
        <AutoVerificationModal
          onClose={() => setShowAutoVerification(false)}
          currentUserId={currentUserId}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Vrácení DTB */}
      {showVraceniDtb && (
        <VraceniDtb
          onClose={() => setShowVraceniDtb(false)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

